import { addressLine } from '../address-words'
import '../new-jobs.css'
import { enquiryAlertsRow } from '../enquiry-alerts'
import { openFollowupDialog } from '../followup-dialog'
import { followupSummary, isFollowupDue } from '../followup'
import { api, changeNow, dismiss, getQueue, mutate, onQueueChange, readCache, type Ack } from '../api'
import { openBooking } from '../booking'
import { bookEnquiry, bookingWaiting } from '../book-enquiry'
import { landFlash } from '../motion'
import {
  addressWithoutPostcode,
  canConvertLead,
  convertBody,
  conversionDraft,
  cycleDaysFromWeeks,
  cycleProposalText,
  findCustomerMatches,
  postcodeAtEnd,
  type ConvertChoice,
} from '../lead-conversion'
import { roundConfig, currencySymbol, currencyWord, postcodeWords, screenLocale } from '../round-config'
import { BOARD_COLUMNS, boardColumn, leadIsOpen, normalizeLeadStatus, sortLeads, leadMatches, leadTone, newerLead, parseCycleWeeks, statusColumnWords, statusOptions, statusSelectValue, statusWords, type LeadFilters, type LeadSortKey, type LeadTone, type SortDir } from '../new-jobs'
import { routeGen } from '../route-state'
import type { Customer, Lead, LeadStatus } from '../types'
import { clear, cycleLabel, el, loadError, money, parseMoney, shortDate, telHref, toast, todayISO, noteDate, noteWords } from '../ui'
import { openWriteQuote } from '../write-quote'
import { markRepliesSeen, quoteReplies, replyWhen } from '../waiting-ok'
import { addDaysISO, daysBetween, enquiryLane, enquiryWords, LET_GO_AFTER_DAYS, letGoOn, messageExcerpt, messagesSent, nudgeDueOn, type EnquiryLane } from '../enquiry-lane'
import { ENQUIRY_SECOND_AFTER_DAYS, enquiryFirstMessage, enquiryNoteLine, nudgeMessageFor, quoted } from '../enquiry-message'
import { isSendPress, linkFor, type NoteChannel } from '../contact'
import { bindEmailSend } from '../email-send'
import { wayIcon } from '../contact-ui'
import { appendNote } from '../rebook-message'
import { undoPatch } from '../undo'
import { byWhatsApp, isLeaving, letGoEnquiry, reasonButtons, slideToLetGo, type LetGoReason } from '../let-go'
import { forgetReady, readyBox, readyKey, readyWords } from '../ready-words'
import { buzz } from '../haptic'
import { businessSettings } from '../business-settings'
import { firstName } from '../late-message'
import { mailSubject } from '../trade'
import { getMe } from '../me'

// New jobs — the workbook's leads tab as an app tab. All 12 sheet columns,
// every imported row (converted ones too — the customer link rides along),
// sortable headers like the grid, and add/edit through the same retry-queue
// machinery. No delete. Conversion is explicit and confirmed-only: a
// CONFIRMED lead with no live customer link offers Convert…, which links an
// existing customer or creates one from the lead's own values — and only
// then, from the saved converted row, an optional Book first visit.

const leads = new Map<number, Lead>()
let followupOnly = false
let filters: LeadFilters = { search: '', status: '', checked: '' }
let sortBy: LeadSortKey = 'quoted_on'
let sortDir: SortDir = 'desc' // newest quote first; missing dates last
let fullSheet = false
let editing = false // a dialog is open — background refreshes must not tear it down
let refreshPending = false
let loadSeq = 0 // latest-request guard: concurrent loads on one route

// This screen's route, checked directly — load() and close-time refreshes
// refuse to act when the browser has moved somewhere else.
// "£45 first, then £35 every 8 weeks"; a Just once or Ad hoc quote (0092) is one price, "£45 a one-off clean" or "£45
// each time", never "first, then" (Sonnet's check of the quotes, 27 Oct 2026).
function quotedWords(l: Lead, style: 'short' | 'long'): string {
  const text = ((l as Lead & { cycle_text?: string | null }).cycle_text ?? '').trim().toLowerCase()
  if (text && l.initial_pence != null) return `${money(l.initial_pence)} ${/^just/.test(text) ? 'a one-off clean' : 'each time'}`
  return [l.initial_pence != null ? `${money(l.initial_pence)} ${style === 'short' ? 'first' : 'for the first clean'}` : '', l.regular_pence != null ? `then ${money(l.regular_pence)}${l.cycle_weeks ? ` every ${plural(l.cycle_weeks, 'week')}` : ''}` : ''].filter(Boolean).join(', ')
}

function onNewJobsRoute(): boolean {
  return location.hash === '#/new-jobs' || location.hash.startsWith('#/new-jobs?')
}

// ONE queue subscription for the whole screen: onQueueChange cannot be
// unsubscribed, so dialogs never register their own — the open dialog (if
// any) is reached through this nullable forwarder and detaches on close.
let dialogQueueChanged: (() => void) | null = null
onQueueChange(() => {
  dialogQueueChanged?.()
  if (!onNewJobsRoute()) return
  if (editing) refreshPending = true
  else void load()
})

const SORT_OPTIONS: [string, string][] = [
  ['quoted_on', 'Sort: date'],
  ['name', 'Sort: customer'],
  ['address', 'Sort: address'],
  ['phone', 'Sort: phone'],
  ['initial_pence', 'Sort: initial £'],
  ['regular_pence', 'Sort: regular £'],
  ['cycle_weeks', 'Sort: cycle'],
  ['status', 'Sort: status'],
  ['initial_lead', 'Sort: initial lead'],
  ['notes', 'Sort: notes'],
  ['checked', 'Sort: checked'],
  ['notes2', 'Sort: notes 2'],
]

const STATUS_LABELS: [LeadTone | '', string][] = [
  ['', 'All statuses'],
  ['confirmed', 'Won'],
  ['quote', 'Quoted'],
  ['maybe', 'Maybe later'],
  ['declined', 'Declined'],
  ['cancelled', 'Cancelled'],
  ['other', 'Other'],
]

interface ColDef {
  // 'email' is not a LeadSortKey (sortLeads lives in src/new-jobs.ts and does
  // not know the field), so the header renders unsortable — see renderTable.
  field: LeadSortKey | 'customer' | 'actions' | 'email'
  label: string
  type?: 'date' | 'money' | 'weeks' | 'bool' | 'status' | 'phone'
  width?: string
}

const COLS: ColDef[] = [
  { field: 'quoted_on', label: 'Date', type: 'date', width: '6.5rem' },
  { field: 'name', label: 'Customer', width: '12rem' },
  { field: 'address', label: 'Address', width: '16rem' },
  { field: 'phone', label: 'Phone', type: 'phone', width: '9rem' },
  { field: 'email', label: 'Email', width: '9rem' },
  { field: 'initial_pence', label: 'Initial', type: 'money', width: '5rem' },
  { field: 'regular_pence', label: 'Regular', type: 'money', width: '5rem' },
  { field: 'cycle_weeks', label: 'Cycle (weeks)', type: 'weeks', width: '6rem' },
  { field: 'status', label: 'Status', type: 'status', width: '11rem' },
  { field: 'initial_lead', label: 'Initial lead', width: '8rem' },
  { field: 'notes', label: 'Notes', width: '14rem' },
  { field: 'checked', label: 'Checked', type: 'bool', width: '4rem' },
  { field: 'notes2', label: 'Notes 2', width: '14rem' },
  { field: 'customer', label: 'Customer', width: '11rem' },
  { field: 'actions', label: 'Edit', width: '4.5rem' },
]

// Outstanding lead mutations, so a reopened screen tells the truth about
// what has not landed yet — and never lets a queued new job be typed twice.
function leadQueue() {
  return getQueue().filter((m) => m.path.startsWith('/api/leads'))
}

function leadHasPendingEdit(id: number): boolean {
  // conflict/failed entries are parked ATtention, not in-flight work — the
  // row may be edited again; a retry/replace supersedes them via dismiss.
  return leadQueue().some((m) => m.method === 'PATCH' && m.path === `/api/leads/${id}` && m.status !== 'conflict' && m.status !== 'failed')
}

// A queued conversion (offline or mid-send). Until it ACKs, the row shows
// "converting…" and cannot be converted, edited or status-changed again.
function leadHasPendingConvert(id: number): boolean {
  return leadQueue().some((m) => m.method === 'POST' && m.path === `/api/leads/${id}/convert` && m.status !== 'conflict' && m.status !== 'failed')
}

// A lead with ANY mutation in flight is busy: a second intent against the
// same row would race its own stale version against the first (edit or
// conversion alike) and 409 against the user's own change.
function leadBusy(id: number): boolean {
  return leadHasPendingEdit(id) || leadHasPendingConvert(id) || leadHasPendingUnlink(id)
}

// A queued Unlink, the same way: until it lands the job keeps its customer and nothing else can change it.
function leadHasPendingUnlink(id: number): boolean {
  return leadQueue().some((m) => m.method === 'POST' && m.path === `/api/leads/${id}/unlink` && m.status !== 'conflict' && m.status !== 'failed')
}

// A saved job's own row from the server takes the place of the copy on screen the moment it lands (Liam's notes, 15 Oct
// 2026: "often not saving"). The list is read again a moment later, and until then every tap on the job was built from
// the version before the one just saved, so a second tap in that gap (another message, a status, Remove) was refused as
// "changed somewhere else" against the owner's own change. False when the screen already holds this or a newer copy.
function keepLanded(id: number, row: unknown): boolean {
  const fresh = row as Lead | null | undefined
  const held = leads.get(id)
  if (!held || !fresh || typeof fresh.version !== 'number' || fresh.version <= held.version) return false
  leads.set(id, { ...held, ...fresh })
  return true
}
// The same for a change that waited for signal and landed from the queue, which has no screen callback of its own.
if (typeof window !== 'undefined') {
  window.addEventListener('roundly:queued-landed', (e) => {
    const d = (e as CustomEvent<{ method?: string; path?: string; row?: unknown }>).detail
    const id = d?.method === 'PATCH' ? /^\/api\/leads\/(\d+)$/.exec(d.path ?? '')?.[1] : undefined
    if (id && keepLanded(Number(id), d.row) && onNewJobsRoute()) refreshListSafely()
  })
}

// Customer ids converted in THIS session: their rows keep a "Book first
// visit" shortcut beside the customer link until it is used. Booking is
// always optional and never automatic — closing or ignoring it keeps the
// link. A lead that gains its live link on a reload (an offline conversion
// that flushed) lands here too.
const freshConversions = new Set<number>()

function contactControl(l: Lead): HTMLButtonElement {
  const b = el('button', { type: 'button', 'aria-label': `Message ${l.name}` }, 'Message')
  b.disabled = leadBusy(l.id)
  b.addEventListener('click', () => openFollowupDialog('leads', l))
  return b
}

// "Write a quote" (quotes by email, step 2): beside Message on an enquiry still open, priced from the
// round's own price list. The panel lives in src/write-quote.ts; a saved quote redraws the board.
function quoteControl(l: Lead): HTMLButtonElement | null {
  if (!leadIsOpen(l)) return null
  const b = el('button', { type: 'button', class: 'newjobs-quote-btn', 'aria-label': `Write a quote for ${l.name}` }, 'Write a quote')
  b.disabled = leadBusy(l.id)
  b.addEventListener('click', () => openWriteQuote(l, () => {
    if (!onNewJobsRoute()) return
    if (editing) refreshPending = true
    else void load()
  }))
  return b
}

function rowsToShow(): Lead[] {
  return sortLeads([...leads.values()].filter((l) => leadMatches(l, filters) && (!followupOnly || isFollowupDue(l)) && !isLeaving(l.id)), sortBy, sortDir)
}

function displayValue(l: Lead, col: ColDef): string {
  const v = l[col.field as keyof Lead]
  if (col.type === 'money') return money(v as number | null)
  if (col.type === 'bool') return (v as number) ? '✓' : ''
  if (col.type === 'weeks') return v === null || v === undefined ? '' : `${v}`
  if (col.type === 'date') return shortDate(v as string | null)
  return v === null || v === undefined ? '' : String(v)
}

// ---- status dropdown (direct edit from the row) ------------------------------

// A parked conflict/failed entry for this lead — attention, not in-flight work.
// Surfaced as a tappable cue on the status cell; dismissed separately in the
// corner queue (reload and dismiss are distinct acts, as everywhere else).
// Conversion attempts park on this lead's /convert path and cue the same way.
function leadAttentionEntry(id: number) {
  return leadQueue().find((m) => (m.path === `/api/leads/${id}` || m.path === `/api/leads/${id}/convert`) && (m.status === 'conflict' || m.status === 'failed')) ?? null
}

function refreshListSafely(): void {
  // A status change resolving must never redraw a newer route's screen.
  if (!onNewJobsRoute()) return
  if (editing) {
    refreshPending = true
    return
  }
  const restoreFocus = captureFocusRestore()
  renderTable(document.querySelector<HTMLElement>('#newjobs-table')!)
  renderCount()
  refreshStatusCounts()
  restoreFocus?.()
}

// The status FILTER's option labels carry live counts ("Quote · 12"); an
// inline status change moves a row between those groups, so the labels are
// recomputed with the redraw — not left stale for the next background load.
function refreshStatusCounts(): void {
  const sel = document.querySelector<HTMLSelectElement>('select[aria-label="Filter by status"]')
  if (!sel) return
  const countBy = (st: LeadStatus | ''): number => [...leads.values()].filter((l) => st === '' || l.status === st).length
  for (const opt of sel.options) {
    const v = opt.value as LeadStatus | ''
    const label = STATUS_LABELS.find(([lv]) => lv === v)?.[1] ?? v
    opt.textContent = v === '' ? label : `${label} · ${countBy(v)}`
  }
}

// The not-saved cue under a status dropdown: names WHY (other owner / server
// rejection), and one tap reloads the list — the parked entry itself is only
// dismissed from the corner queue, never here. The wording demands a
// deliberate retry: nothing rebase-sends the choice automatically.
function statusAttentionCue(entry: { status: string; error?: string; label: string }): HTMLElement {
  const cue = el(
    'button',
    { type: 'button', class: 'newjobs-status-alert' },
    entry.status === 'conflict'
      ? `Earlier change (${entry.label}) was not saved: it was changed somewhere else first. Reload to review.`
      : `Earlier change (${entry.label}) was not saved: ${entry.error ?? 'rejected'}. Reload to review.`,
  )
  cue.addEventListener('click', () => void load())
  return cue
}

// Marked Quoted by hand (the quote went another way): that counts as the message out, so the nudge comes five days on
// (10 Oct 2026). Only when nothing was counted yet; any other status change leaves the count alone.
function quotedByHand(l: Lead, next: string): Record<string, unknown> {
  if (normalizeLeadStatus(next) !== 'quote' || leadTone(l) === 'quote' || messagesSent(l) > 0) return {}
  const today = todayISO()
  const note = appendNote(l.contact_note, `${noteDate(today)}: quoted`)
  return { enquiry_messages: 1, enquiry_messaged_on: today, ...(note !== (l.contact_note ?? '') ? { contact_note: note } : {}) }
}

// Change a lead's status from the row itself. Only status_raw is sent (and, marked Quoted by hand, the message count), with the
// version — nothing optimistic: the row keeps showing the stored wording until
// the server's own row comes back, and the pending/queued/conflict states ride
// the same machinery the Edit dialog uses (mutate queue + queue banner + chip).
function commitStatus(l: Lead, next: string): void {
  const gen = routeGen // an ack after navigation must not touch the newer screen
  lastMovedLead = l.id
  if (normalizeLeadStatus(next) === 'confirmed' && normalizeLeadStatus(l.status_raw) !== 'confirmed') pendingWins.add(l.id)
  void mutate({
    label: `Status ${statusWords(next)}: ${l.name}`,
    method: 'PATCH',
    path: `/api/leads/${l.id}`,
    body: { version: l.version, status_raw: next === '' ? null : next, ...quotedByHand(l, next) },
    onAck: (ack) => {
      if (gen !== routeGen || !onNewJobsRoute()) return
      // Only apply the server's row if it is not older than what we hold —
      // a background refresh may already have landed a newer version.
      const held = leads.get(l.id)
      const fresh = ack.row as Lead
      if (held && fresh.version < held.version) return
      leads.set(l.id, { ...held!, ...fresh })
      // On a phone the card now lives in another tab: follow it there.
      if (!fullSheet) boardTab = boardColumn(fresh)
      landedLead = l.id
      window.clearTimeout(landedTimer)
      landedTimer = window.setTimeout(() => { landedLead = null }, 1500)
      refreshListSafely()
      // Only once it has saved: a small moment when a job is won.
      if (pendingWins.delete(l.id) && normalizeLeadStatus(fresh.status_raw) === 'confirmed') toast(WIN_TOAST(l.name))
    },
  }).then((ack) => {
    if (gen !== routeGen || !onNewJobsRoute()) return
    // 409 parks as 'conflict' (attention cue shows and demands an explicit
    // retry), offline/5xx stays queued (row stays pending) — both already
    // truthful states. A 4xx rejection never queued: redraw restores the
    // stored wording and the cue names why.
    if (!ack.ok && ack.status >= 400 && ack.status < 500 && ack.status !== 409) {
      refreshListSafely()
      window.alert(ack.error ?? 'rejected')
    }
  })
}

// The Status column, as a directly usable dropdown: exact workbook wordings
// plus whatever custom values exist in the data, the row's own value always
// selectable, disabled while this row has a save in flight (Edit included).
function statusControl(l: Lead): HTMLElement {
  // tabindex -1: not a tab stop, but focusable programmatically — the pending
  // state's disabled dropdown needs somewhere for keyboard focus to sit.
  const wrap = el('span', { class: 'newjobs-status', tabindex: '-1' })
  const current = statusSelectValue(l.status_raw)
  const pending = leadBusy(l.id)
  const sel = el('select', {
    class: `newjobs-status-select newjobs-status-${leadTone(l)}`,
    'aria-label': `Status for ${l.name}`,
    title: `${statusWords(l.status_raw)} · Counts as: ${statusColumnWords(l.status_raw)}`,
    ...(pending ? { disabled: 'disabled' } : {}),
  })
  sel.append(el('option', { value: '' }, statusWords('')))
  for (const o of statusOptions(l.status_raw)) sel.append(el('option', { value: o }, statusWords(o)))
  sel.value = current
  sel.addEventListener('change', () => {
    const next = sel.value
    if (next === current) return
    // Re-check synchronously, not just the render-time disabled state: a
    // stale control (a redraw raced the pick, or a queued Edit dialog save)
    // must never mint a second intent for the same row.
    if (leadBusy(l.id)) {
      sel.value = current
      return
    }
    commitStatus(l, next)
    refreshListSafely() // redraw as pending on the STORED wording — never a premature "saved"
  })
  wrap.append(sel)
  const attention = leadAttentionEntry(l.id)
  if (attention) wrap.append(statusAttentionCue(attention))
  return wrap
}

// The linked-customer affordances, shared by the table cell and the card:
// the live link (a link to a deleted customer is a dead end, and dead ends
// read as broken — it reads as unconverted instead), an optional Book first
// visit shortcut right after a conversion landed, a Convert… button for a
// confirmed unlinked lead, and — while a conversion is queued — an honest
// "converting…" rather than a premature link. A linked job also offers Unlink (Lewis, 14 Oct 2026); on a card the
// customer's name says what it is ("Customer: Daisy"), since a bare name beside the buttons read as a second person.
function customerLinkControls(l: Lead, card = false): HTMLElement[] {
  if (l.live_customer_id) {
    const cid = l.live_customer_id
    const link = el('a', { href: `#/customer/${cid}`, class: 'name-link' }, l.customer_name ?? 'Open customer')
    const unlink = el('button', { type: 'button', class: card ? 'newjobs-unlink-btn nj-outcome nj-outcome-quiet' : 'newjobs-unlink-btn', 'aria-label': `Unlink ${l.name} from ${l.customer_name ?? 'this customer'}` }, 'Unlink') as HTMLButtonElement
    if (leadBusy(l.id)) unlink.disabled = true
    else unlink.addEventListener('click', () => openUnlink(l, unlink))
    const nodes: HTMLElement[] = [card ? el('span', { class: 'newjobs-linked' }, 'Customer: ', link) : link, unlink]
    if (freshConversions.has(cid)) {
      const book = el(
        'button',
        { type: 'button', class: 'newjobs-bookfirst-btn', 'aria-label': `Book first visit for ${l.customer_name ?? l.name}` },
        'Book first visit',
      )
      book.addEventListener('click', () => {
        // Optional, explicit, offered once: the shortcut goes, the link stays.
        freshConversions.delete(cid)
        openBooking({ customerId: cid })
        refreshListSafely()
      })
      nodes.push(book)
    }
    return nodes
  }
  if (leadHasPendingConvert(l.id)) return [el('span', { class: 'muted' }, 'converting…')]
  if (canConvertLead(l) && !leadBusy(l.id)) {
    const convert = el(
      'button',
      { type: 'button', class: 'newjobs-convert-btn', 'aria-label': `Make ${l.name} a customer` },
      'Make them a customer',
    )
    convert.addEventListener('click', () => openConvert(l, convert))
    return [convert]
  }
  return []
}

function renderCell(l: Lead, col: ColDef, td: HTMLTableCellElement): void {
  clear(td)
  td.className = ''
  td.dataset.field = String(col.field)
  if (col.field === 'name') {
    const open = el('button', { type: 'button', class: 'newjobs-open-job',
      'data-edit-lead': String(l.id), 'aria-label': `Edit job for ${l.name}` }, l.name)
    open.disabled = leadBusy(l.id)
    open.addEventListener('click', () => openDialog(l, open))
    td.append(open)
    if (l.came_by === 'email') td.append(el('span', { class: 'newjobs-by-email' }, 'By email'))
    if ((l.replies_unseen ?? 0) > 0) td.append(el('span', { class: 'newjobs-replied' }, 'New reply'))
    else if (quoteReplies(l).length) td.append(el('span', { class: 'newjobs-by-email' }, 'Replied'))
    return
  }
  if (col.field === 'customer') {
    td.append(...customerLinkControls(l))
    return
  }
  if (col.field === 'actions') {
    const edit = el('button', { type: 'button', class: 'newjobs-edit-btn', 'aria-label': `Edit job for ${l.name}` }, 'Edit')
    if (leadBusy(l.id)) {
      edit.setAttribute('disabled', 'disabled')
      edit.title = 'Still saving. One edit at a time.'
    } else {
      edit.addEventListener('click', () => openDialog(l, edit))
    }
    td.append(edit, contactControl(l))
    const quoteBtn = quoteControl(l)
    if (quoteBtn) td.append(quoteBtn)
    const summary = followupSummary(l)
    if (summary) td.append(el('span', { class: 'cell-secondary' }, summary))
    return
  }
  if (col.type === 'status') {
    td.append(statusControl(l))
    return
  }
  if (col.type === 'phone') {
    const href = telHref(l.phone)
    if (href) {
      td.append(el('a', { href, class: 'name-link' }, l.phone ?? ''))
      return
    }
    td.textContent = l.phone ?? ''
    return
  }
  // Long notes are cut to two lines so a row stays one job high; the whole text is on hover and in Edit.
  if (col.field === 'notes' || col.field === 'notes2' || col.field === 'initial_lead') {
    const full = displayValue(l, col)
    if (full) td.append(el('div', { class: 'newjobs-clip', title: full }, full))
    return
  }
  td.textContent = displayValue(l, col)
}

function makeCell(l: Lead, col: ColDef): HTMLTableCellElement {
  const td = el('td', { class: `newjobs-col-${col.field}` })
  renderCell(l, col, td)
  return td
}

function applySort(key: string): void {
  if (sortBy === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc'
  else {
    sortBy = key as LeadSortKey
    sortDir = 'asc'
  }
  renderTable(document.querySelector<HTMLElement>('#newjobs-table')!)
  renderCount()
  syncSortControls()
  // re-render rebuilt the header buttons: put focus back so Enter reverses
  // the same column again (same contract as the grid).
  document.querySelector<HTMLButtonElement>(`#newjobs-table button[data-sort-key="${key}"]`)?.focus({ preventScroll: true })
}

function syncSortControls(): void {
  const sel = document.querySelector<HTMLSelectElement>('select[aria-label="Sort new jobs"]')
  if (sel) sel.value = sortBy
  const dir = document.querySelector<HTMLButtonElement>('button[data-sort-dir]')
  if (dir) {
    dir.textContent = sortDir === 'desc' ? '↓ descending' : '↑ ascending'
    dir.setAttribute('aria-label', `Sort direction: ${sortDir === 'desc' ? 'descending' : 'ascending'}. Activate to reverse.`)
  }
}

function renderTable(container: HTMLElement): void {
  const position = { top: container.scrollTop, left: container.scrollLeft }
  clear(container)
  const table = el('table', { class: 'sheet', 'aria-label': 'New jobs' })
  const thead = el('thead', {})
  const headRow = el('tr', {})
  for (const col of COLS) {
    // 'email' has no sort key behind it (the field is new; sortLeads does not
    // read it yet), so its header renders as a plain label — a sort button
    // that silently did nothing would be worse than none.
    const sortable = col.field !== 'customer' && col.field !== 'actions' && col.field !== 'email'
    const th = el('th', { scope: 'col', ...(sortable ? { class: 'th-sort-cell' } : {}), ...(col.width ? { style: `min-width:${col.width}` } : {}) })
    if (!sortable) {
      th.textContent = col.label
      headRow.append(th)
      continue
    }
    const active = sortBy === col.field
    th.setAttribute('aria-sort', active ? (sortDir === 'desc' ? 'descending' : 'ascending') : 'none')
    const btn = el(
      'button',
      {
        type: 'button',
        class: `th-sort${active ? ' th-sort-active' : ''}`,
        'data-sort-key': col.field,
        'aria-label': active ? `Sort by ${col.label}, currently ${sortDir === 'desc' ? 'descending' : 'ascending'}` : `Sort by ${col.label}`,
      },
      col.label,
      el('span', { class: 'sort-arrow', 'aria-hidden': 'true' }, active ? (sortDir === 'desc' ? '↓' : '↑') : ''),
    )
    btn.addEventListener('click', () => applySort(col.field))
    th.append(btn)
    headRow.append(th)
  }
  thead.append(headRow)
  table.append(thead)
  const tbody = el('tbody', {})
  const rows = rowsToShow()
  for (const l of rows) {
    const tr = el('tr', { 'data-lead-id': String(l.id), class: `newjobs-row-${leadTone(l)}` })
    if (l.status === 'cancelled' || l.status === 'declined') tr.classList.add('newjobs-row-dead')
    if (leadHasPendingEdit(l.id)) tr.classList.add('newjobs-row-pending')
    for (const col of COLS) tr.append(makeCell(l, col))
    tbody.append(tr)
  }
  table.append(tbody)
  container.append(table)
  container.scrollTop = position.top
  container.scrollLeft = position.left
  renderCards()
  const empty = document.querySelector<HTMLElement>('#newjobs-empty')
  if (empty) {
    empty.hidden = rows.length > 0
    empty.textContent = 'No jobs match. Clear filters to see more.'
  }
}

// ---- the list and one enquiry (New jobs redesign, Liam 10 Oct 2026, drawing A:
// https://claude.ai/artifact/FwSzgYQbf125GgPaqczJWh). "We want to be able to easily and satisfyingly reply to new
// customers and follow up on no replies." One list: the people to answer first, longest waiting first, then the people
// to chase; everyone else folded under their stage. Tap a person for their page: their message (tap to open or hide),
// Call, WhatsApp, Text, Email, Write a quote, what has happened, and what happens next. On a computer the list and the
// page sit side by side. Where each person sits is src/enquiry-lane.ts. The full sheet keeps every column and filter.

// The phone has no board tabs any more; kept so an old status save's follow-up has somewhere harmless to write.
let boardTab: string = 'new'
// An enquiry to show when New jobs opens (Needs you's "Open in New jobs" on a reply, a phone alert): its page opens.
// Forgotten once shown.
let revealLead: number | null = null
export function openNewJobsAt(leadId: number): void {
  revealLead = leadId
  window.location.hash = '#/new-jobs'
}
// Whose page is open, and whose message is shown in full. Kept across redraws.
let openLead: number | null = null
let openClosedByHand = false
const messageShown = new Set<number>()
const foldsOpen = new Set<string>()
let lastMovedLead: number | null = null
// The lead whose save has just landed, for the landing glow (cleared after 1.5s).
let landedLead: number | null = null
let landedTimer = 0
const pendingWins = new Set<number>()
const WIN_TOAST = (name: string): string => `Nice one. ${name} said yes. Tap "Make them a customer" to add them.`

const LANE_TITLE: Record<EnquiryLane, string> = {
  answer: 'To answer', chase: 'To chase', waiting: 'Waiting on them', letgo: 'Let go: no answer', later: 'Maybe later',
  won: 'Won', closed: 'Not going ahead', sorting: 'Needs sorting',
}

function phoneLayout(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 760px)').matches
}

// "Wed 15 Oct".
function dayWords(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString(screenLocale(), { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`
}

// The small coloured words beside a name: how long they have waited, or what happens next.
function lanePill(l: Lead, lane: EnquiryLane, today: string): { text: string; tone: 'late' | 'fresh' | 'calm' } | null {
  if (lane === 'answer') {
    if ((l.replies_unseen ?? 0) > 0) return { text: 'replied', tone: 'late' }
    if (!l.quoted_on) return null
    const d = daysBetween(l.quoted_on, today)
    return d <= 0 ? { text: 'today', tone: 'fresh' } : { text: `waiting ${plural(d, 'day')}`, tone: 'late' }
  }
  if (lane === 'chase') {
    const d = l.enquiry_messaged_on ? daysBetween(l.enquiry_messaged_on, today) : 0
    return { text: `no answer ${plural(d, 'day')}`, tone: 'late' }
  }
  if (lane === 'waiting') {
    const n = messagesSent(l)
    const due = n === 1 ? nudgeDueOn(l) : n >= 2 ? letGoOn(l) : null
    if (due) return { text: n === 1 ? `nudge ${shortDate(due)}` : `let go ${shortDate(due)}`, tone: 'calm' }
    return null
  }
  if (lane === 'letgo') return { text: 'no answer', tone: 'calm' }
  return null
}

// Postcode (or the address), what the property is, and how they found the business: "N10 2AB · Flat · website form".
function whereLine(l: Lead): string {
  const words = enquiryWords(l)
  const detail = (k: string): string | null => words.details.find(([key]) => key === k)?.[1] ?? null
  const place = detail('Postcode') ?? postcodeAtEnd(l.address, roundConfig().country) ?? l.address
  const how = l.came_by === 'email' ? (detail('From') ? 'website form' : 'by email') : words.note
  return [place, detail('Property'), how && how.length <= 40 ? how : null].filter(Boolean).join(' · ')
}

// The words a tap on WhatsApp, Text or Email opens with: the first reply while they wait for one, the nudge once it is
// due. Anywhere else the buttons just open the conversation, with nothing written and nothing counted.
// The usual words for this moment (Your messages), before any change in the box.
function usualWords(l: Lead, lane: EnquiryLane): string | null {
  // They wrote back: the answer is the owner's own, so the way opens with nothing written (it still counts).
  if (lane === 'answer' && (l.replies_unseen ?? 0) > 0) return null
  if (lane === 'answer') return enquiryFirstMessage(l.name)
  if (lane === 'chase') return nudgeMessageFor(l)
  return null
}
const readyKeyFor = (l: Lead, lane: EnquiryLane): string => readyKey(l.id, lane === 'chase' ? 'nudge' : 'first')
// What WhatsApp, Text or Email open with: the box's words, changed or not (src/ready-words.ts).
function wordsToSend(l: Lead, lane: EnquiryLane): string | null {
  const usual = usualWords(l, lane)
  return usual === null ? null : readyWords(readyKeyFor(l, lane), usual)
}

function callGlyph(): SVGElement {
  const svg = wayIcon('text')
  svg.querySelector('path')?.setAttribute('d', 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2')
  return svg
}

// Taps on this enquiry still waiting to save: offline, a second tap must count two messages, not one again.
function queuedSends(path: string): number {
  // A send stamps "last contacted"; "They replied" does not, so it is never counted as a message here.
  return getQueue().filter((q) => q.path === path && q.method === 'PATCH' && (q.status === 'queued' || q.status === 'sending') && !!q.body && 'enquiry_messages' in (q.body as Record<string, unknown>) && 'last_contacted_on' in (q.body as Record<string, unknown>)).length
}

// The sent moment: the card goes green with a tick and the phone buzzes, on the tap itself (a phone only buzzes for a
// person's tap). The save follows; the list then shows them in their new place, glowing once.
function sentMoment(from: HTMLElement): void {
  const host = from.closest<HTMLElement>('.nj-card, .nj-detail')
  if (host) {
    host.classList.remove('nj-sent')
    void host.offsetWidth
    host.classList.add('nj-sent')
  }
  buzz('success')
}

// One tap on WhatsApp, Text or Email in To answer or To chase counts one message sent, the same save Messages makes
// (rebook.ts recordEnquiryMessage), so both screens move them on together: one send action (functions/lib/enquiry-send.ts;
// tidy-up items 6 and 9, 20 Oct 2026) that keeps the words, reads up to what this phone had seen from them and takes
// their replies off down to any that came after. Sent by Roundly itself (keepWords false), the words are kept already and
// the count is saved as before.
function recordSend(l: Lead, channel: NoteChannel, from: HTMLElement, words: string | null, keepWords = true): void {
  const path = `/api/leads/${l.id}`
  // Answering their reply starts a fresh wait (one message out, the nudge in five days), rather than being the last try.
  const n = (l.replies_unseen ?? 0) > 0 ? 1 : messagesSent(l) + queuedSends(path) + 1
  const today = todayISO()
  const note = appendNote(l.contact_note, enquiryNoteLine(noteDate(today), n, channel))
  const body: Record<string, unknown> = { version: l.version, enquiry_messages: n, enquiry_messaged_on: today, last_contacted_on: today }
  if (note !== (l.contact_note ?? '')) body.contact_note = note
  if (keepWords) body.sent = { way: channel === 'WhatsApp' ? 'whatsapp' : channel, words, upto: l.last_in_id ?? 0 }
  sentMoment(from)
  // Sent by Roundly: answering a reply is seeing it (Needs you stops listing it), but only up to what this phone had seen
  // from them; one that came after stays to answer and unread (Astra's review, round 4, 20 Oct 2026).
  if (!keepWords && (l.replies_unseen ?? 0) > 0) void mutate({ label: `Seen the reply from ${l.name}`, method: 'POST', path: `${path}/replies-seen`, body: { upto: l.last_in_id ?? 0 } })
  const gen = routeGen
  void mutate({
    label: `Enquiry ${channel} ${n}: ${l.name}`,
    method: 'PATCH',
    path,
    body,
    onAck: (ack) => {
      if (!ack.ok && ack.status >= 400 && ack.status < 500) window.alert(ack.error ?? 'rejected')
      if (ack.ok) { landedLead = l.id; window.clearTimeout(landedTimer); landedTimer = window.setTimeout(() => { landedLead = null }, 1500) }
      const next = n === 1
        ? `No answer by ${dayWords(addDaysISO(today, ENQUIRY_SECOND_AFTER_DAYS))}? They move to To chase.`
        : `No answer by ${dayWords(addDaysISO(today, LET_GO_AFTER_DAYS))}? They move to Maybe later.`
      // Opened in the phone's own app (keepWords): Roundly cannot see it go, so it says so (Liam, 10 Oct 2026: "did it
      // go if not the UI doesnt make it clear").
      const opened = channel === 'email' ? 'Your email' : channel === 'text' ? 'Messages' : 'WhatsApp'
      conversations.delete(l.id)
      // Undo puts that send back on the server, exactly (functions/lib/enquiry-send.ts), while it is still the latest change.
      const sentKey = (ack.row as { sent_key?: unknown } | undefined)?.sent_key
      if (keepWords && typeof sentKey === 'string') undoPatch({ text: `${opened} opened for ${l.name}: press send there. ${next} Not sent? Undo.`, label: `Undo enquiry ${channel}: ${l.name}`, path, ack, version: l.version,
        body: { unsend: sentKey }, after: () => { conversations.delete(l.id); if (onNewJobsRoute()) void load() } })
      else if (!keepWords) undoPatch({ text: `Sent to ${l.name}. ${next}`, label: `Undo enquiry ${channel}: ${l.name}`, path, ack, version: l.version,
        body: { enquiry_messages: l.enquiry_messages ?? 0, enquiry_messaged_on: l.enquiry_messaged_on ?? null, last_contacted_on: l.last_contacted_on ?? null, contact_note: l.contact_note ?? null },
        after: () => { if (onNewJobsRoute()) void load() } })
      if (ack.ok && keepLanded(l.id, ack.row) && gen === routeGen && onNewJobsRoute()) refreshListSafely()
      if (gen === routeGen && onNewJobsRoute()) void load()
    },
    onConflict: () => { if (onNewJobsRoute()) void load() },
  })
}

// "They replied" (WhatsApp, a text, a call): the conversation carries on outside Roundly, so the wait starts again
// from today, one message out. Nothing else changes; Undo puts the count and date back.
function theyReplied(l: Lead, button: HTMLButtonElement): void {
  button.disabled = true
  const path = `/api/leads/${l.id}`
  const today = todayISO()
  const note = appendNote(l.contact_note, `${noteDate(today)}: they replied`)
  const body: Record<string, unknown> = { version: l.version, enquiry_messages: 1, enquiry_messaged_on: today }
  if (note !== (l.contact_note ?? '')) body.contact_note = note
  // An emailed or texted reply waiting is the one they made: seen now.
  // Up to what this phone had seen from them: a reply that came after stays to answer (Astra's review, round 4).
  if ((l.replies_unseen ?? 0) > 0) void mutate({ label: `Seen the reply from ${l.name}`, method: 'POST', path: `${path}/replies-seen`, body: { upto: l.last_in_id ?? 0 } })
  void mutate({
    label: `They replied: ${l.name}`,
    method: 'PATCH',
    path,
    body,
    onAck: (ack) => {
      if (!ack.ok && ack.status >= 400 && ack.status < 500) { button.disabled = false; window.alert(ack.error ?? 'rejected') }
      undoPatch({ text: `Noted. ${l.name} is waiting on you to carry on; no nudge before ${dayWords(addDaysISO(today, ENQUIRY_SECOND_AFTER_DAYS))}.`, label: `Undo they replied: ${l.name}`, path, ack, version: l.version,
        body: { enquiry_messages: l.enquiry_messages ?? 0, enquiry_messaged_on: l.enquiry_messaged_on ?? null, contact_note: l.contact_note ?? null },
        after: () => { if (onNewJobsRoute()) void load() } })
      if (ack.ok && keepLanded(l.id, ack.row) && onNewJobsRoute()) refreshListSafely()
      if (onNewJobsRoute()) void load()
    },
    onConflict: () => { if (onNewJobsRoute()) void load() },
  })
}

// Call, WhatsApp, Text and Email: each opens the phone's own app. In To answer and To chase the message is written
// ready and the tap counts it; anywhere else they only open the conversation. A way with no number or address is
// left out on a card and greyed with the reason on the enquiry's page.
function waysRow(l: Lead, lane: EnquiryLane, big: boolean, sending?: { text: boolean; whatsapp: boolean }): HTMLElement {
  const row = el('div', { class: `nj-ways${big ? ' nj-ways-big' : ''}`, role: 'group', 'aria-label': `Contact ${l.name}` })
  const words = wordsToSend(l, lane)
  const counts = (lane === 'answer' || lane === 'chase') && !leadBusy(l.id) && leadIsOpen(l)
  const subject = quoted(l) ? mailSubject('quote') : mailSubject('enquiry')
  const ways: { way: 'call' | 'whatsapp' | 'text' | 'email'; label: string; href: string | null; missing: string }[] = [
    { way: 'call', label: 'Call', href: telHref(l.phone), missing: 'No phone' },
    { way: 'whatsapp', label: 'WhatsApp', href: linkFor('whatsapp', l, words ?? '', subject).href, missing: 'No mobile' },
    { way: 'text', label: 'Text', href: linkFor('text', l, words ?? '', subject).href, missing: 'No mobile' },
    { way: 'email', label: 'Email', href: linkFor('email', l, words ?? '', subject).href, missing: 'No email' },
  ]
  for (const w of ways) {
    if (!w.href) {
      if (big) row.append(el('span', { class: `nj-way nj-way-${w.way} nj-way-off`, 'aria-disabled': 'true' }, w.way === 'call' ? callGlyph() : wayIcon(w.way), el('span', {}, w.missing)))
      continue
    }
    const fromRoundly = (w.way === 'text' || w.way === 'whatsapp') && sending?.[w.way] && leadIsOpen(l)
    if (fromRoundly) {
      const b = el('button', { type: 'button', class: `nj-way nj-way-${w.way}`, 'aria-label': `${w.label} ${l.name} from Roundly` })
      b.append(wayIcon(w.way as 'text' | 'whatsapp'))
      if (big) b.append(el('span', {}, w.label))
      b.addEventListener('click', () => sendFromRoundly(l, w.way as 'text' | 'whatsapp', wordsToSend(l, lane) ?? '', lane, b))
      row.append(b)
      continue
    }
    const a = el('a', { href: w.href, class: `nj-way nj-way-${w.way}`, 'aria-label': `${w.label} ${l.name}`, ...(w.way === 'whatsapp' ? { target: '_blank', rel: 'noopener' } : {}) })
    a.append(w.way === 'call' ? callGlyph() : wayIcon(w.way))
    if (big) a.append(el('span', {}, w.label))
    if (w.way !== 'call') {
      // The words as the box has them at the tap (changed after this was drawn, or not).
      const fresh = (): string | null => {
        const now = wordsToSend(l, lane)
        const href = linkFor(w.way as 'whatsapp' | 'text' | 'email', l, now ?? '', subject).href
        if (href) a.setAttribute('href', href)
        return now
      }
      const channel: NoteChannel = w.way === 'whatsapp' ? 'WhatsApp' : w.way
      bindEmailSend(a, () => {
        if (leadBusy(l.id)) { toast('Still saving that job. Try again in a moment.'); return null }
        const now = fresh(), href = a.getAttribute('href')
        return href ? { href, sent: () => {
          if (counts) { recordSend(l, channel, a, now); forgetReady(readyKeyFor(l, lane)) }
        } } : null
      })

    }
    row.append(a)
  }
  return row
}

// The enquiry's conversation (GET /api/leads/:id/messages, 0058), read when their page opens and kept for the visit.
// `action`: kept by a send from an enquiry's card (functions/lib/enquiry-send.ts), whose note line is `counted`.
interface Kept { id: number; direction: 'in' | 'out'; way: 'whatsapp' | 'text' | 'email' | 'call' | 'form'; body: string | null; state: string; at: string; action?: boolean; counted?: string | null }
const conversations = new Map<number, { messages: Kept[]; sending: { text: boolean; whatsapp: boolean } } | 'loading'>()
function conversationOf(l: Lead): { messages: Kept[]; sending: { text: boolean; whatsapp: boolean } } | null {
  const held = conversations.get(l.id)
  if (held && held !== 'loading') return held
  if (!held) {
    conversations.set(l.id, 'loading')
    void api<{ messages: Kept[]; sending: { text: boolean; whatsapp: boolean } }>(`/api/leads/${l.id}/messages`)
      .then((c) => { conversations.set(l.id, c); if (onNewJobsRoute() && openLead === l.id && !editing) renderCards() })
      .catch(() => { conversations.delete(l.id) })
  }
  return null
}

// A text or WhatsApp sent by Roundly itself, once Twilio is set up (0058; until then the buttons open the phone's apps).
// The words are shown to change first; it goes only on Send.
function sendFromRoundly(l: Lead, way: 'text' | 'whatsapp', words: string, lane: EnquiryLane, from: HTMLElement): void {
  const dialog = el('dialog', { class: 'booking-dialog nj-send-dialog', 'aria-labelledby': 'nj-send-title' })
  const text = el('textarea', { rows: '8', 'aria-label': 'Your message', class: 'nj-send-text' }) as HTMLTextAreaElement
  text.value = words
  const note = el('p', { class: 'form-error', role: 'alert' })
  const send = el('button', { type: 'button', class: 'btn-primary' }, way === 'text' ? 'Send the text' : 'Send the WhatsApp')
  const cancel = el('button', { type: 'button', class: 'btn-secondary' }, 'Cancel')
  dialog.append(el('header', { class: 'booking-head' }, el('h2', { id: 'nj-send-title' }, `${way === 'text' ? 'Text' : 'WhatsApp'} ${l.name}`)),
    el('div', { class: 'booking-content' }, el('p', { class: 'muted' }, `Goes from Roundly's number. Their answer comes back here.`), text, note, el('div', { class: 'nj-outcomes' }, send, cancel)))
  cancel.addEventListener('click', () => dialog.close())
  dialog.addEventListener('close', () => dialog.remove())
  // One key per attempt: a retry after "we couldn't confirm" keeps it (never sent twice); a definite refusal starts afresh.
  let key = crypto.randomUUID()
  send.addEventListener('click', () => {
    if (!text.value.trim()) return
    send.disabled = true
    note.textContent = ''
    // WhatsApp outside a day of their own WhatsApp: the server sends the approved wording for this moment instead.
    const template = way === 'whatsapp' ? { template: lane === 'chase' ? 'nudge' : 'first', first_name: firstName(l.name), sign_off: businessSettings().booking_signoff } : {}
    // What this phone had seen from them: once the text has gone, Chats reads up to it, never past it (tidy-up item 9,
    // Sonnet's edge case S E1, 20 Oct 2026).
    void changeNow<{ row?: Kept; error?: string }>('POST', `/api/leads/${l.id}/send`, { way, body: text.value, ...template, ...(l.last_in_id ? { upto: l.last_in_id } : {}) }, key)
      .then(() => {
        dialog.close()
        conversations.delete(l.id)
        if (lane === 'answer' || lane === 'chase') recordSend(l, way === 'text' ? 'text' : 'WhatsApp', from, text.value, false)
        else { sentMoment(from); toast(`Sent to ${l.name}.`); if (onNewJobsRoute()) void load() }
      })
      .catch((err: Error) => {
        send.disabled = false
        note.textContent = err.message || "It didn't go. Try again."
        if ((err as { code?: string }).code === 'send_refused') key = crypto.randomUUID()
      })
  })
  document.body.append(dialog)
  dialog.showModal()
}

function quoteButton(l: Lead, label: string, cls: string): HTMLButtonElement {
  const b = el('button', { type: 'button', class: cls, 'aria-label': `Write a quote for ${l.name}` }, label)
  b.disabled = leadBusy(l.id)
  b.addEventListener('click', () => openWriteQuote(l, () => {
    // Saved: the quote counts as a message, so they move to Waiting on them. The same moment as a send.
    buzz('success')
    landedLead = l.id
    if (!onNewJobsRoute()) return
    if (editing) refreshPending = true
    else void load()
  }))
  return b
}

// Opening someone shows their page (a phone: the whole screen, at the top); closing goes back to their row in the list.
function openEnquiry(id: number | null): void {
  const was = openLead
  openLead = id
  openClosedByHand = id === null
  renderCards()
  if (id !== null) {
    if (phoneLayout() && typeof window.scrollTo === 'function') window.scrollTo({ top: 0 })
    document.querySelector<HTMLElement>('.nj-detail h3')?.focus({ preventScroll: true })
  } else if (was !== null) {
    const row = document.querySelector<HTMLElement>(`.nj-card[data-lead-id="${was}"] .nj-card-head, .nj-row[data-lead-id="${was}"]`)
    row?.focus()
    row?.scrollIntoView?.({ block: 'center' })
  }
}

// One person in a list: a tap anywhere on the top opens their page.
function laneCard(l: Lead, lane: EnquiryLane, today: string): HTMLElement {
  const card = el('article', { class: `nj-card nj-card-${lane}${openLead === l.id ? ' nj-card-on' : ''}${leadBusy(l.id) ? ' nj-card-saving' : ''}`, 'data-lead-id': String(l.id) })
  const pill = lanePill(l, lane, today)
  const head = el('button', { type: 'button', class: 'nj-card-head', 'aria-label': `Open ${l.name}`, 'aria-expanded': String(openLead === l.id) },
    el('span', { class: 'nj-card-name' }, l.name),
    pill ? el('span', { class: `nj-pill nj-pill-${pill.tone}` }, pill.text) : '',
    el('span', { class: 'nj-card-where' }, whereLine(l) || (l.phone ?? '')))
  head.addEventListener('click', () => openEnquiry(openLead === l.id && phoneLayout() ? null : l.id))
  card.append(head)
  const said = messageExcerpt(enquiryWords(l).message)
  const reply = quoteReplies(l).at(-1)
  if ((l.replies_unseen ?? 0) > 0 && reply) card.append(el('p', { class: 'nj-card-said nj-card-reply' }, `Replied: “${messageExcerpt(reply.body, 90)}”`))
  else if (said && (lane === 'answer' || lane === 'chase')) card.append(el('p', { class: 'nj-card-said' }, `“${said}”`))
  if (lane === 'chase') {
    const n = messagesSent(l)
    const priced = quotedWords(l, 'short')
    card.append(el('p', { class: 'nj-card-line' }, `${quoted(l) ? `Quoted${priced ? ` ${priced}` : ''}` : 'First reply sent'}${l.enquiry_messaged_on ? ` · sent ${shortDate(l.enquiry_messaged_on)}` : ''}${n === 1 ? ' · one gentle nudge left' : ''}`))
  }
  if (lane === 'answer' || lane === 'chase') {
    const acts = el('div', { class: 'nj-card-acts' }, waysRow(l, lane, false))
    if (lane === 'answer' && !quoted(l)) acts.append(quoteButton(l, 'Quote', 'nj-card-main'))
    else {
      const open = el('button', { type: 'button', class: 'nj-card-main nj-card-main-quiet', 'aria-label': `See the nudge for ${l.name}` }, lane === 'chase' ? 'Nudge' : 'Open')
      open.addEventListener('click', () => openEnquiry(l.id))
      acts.append(open)
    }
    card.append(acts)
  }
  // Slide left to let it go: Spam, Not our area, Not interested (src/let-go.ts), on a phone, while it is still open.
  if (phoneLayout() && leadIsOpen(l)) slideToLetGo(card, l.name, (reason) => { if (letGoLead(l, reason) === false) refreshListSafely() }, () => leadBusy(l.id))
  return card
}

// An enquiry swiped away, or let go from its page (src/let-go.ts). Its page closes; the lists reload once it has saved.
function letGoLead(l: Lead, reason: LetGoReason): false | void {
  // The job's last change is still on its way, so Remove would go on the version before it and be refused as changed
  // somewhere else (Liam's notes, 15 Oct 2026). Said plainly, the three reasons left as they are; they tap again once it
  // has saved. (A slid card has already folded away: its caller draws it back.)
  if (leadBusy(l.id)) {
    toast('Still saving that job. Try again in a moment.')
    return false
  }
  const gen = routeGen
  if (openLead === l.id) { openLead = null; openClosedByHand = true }
  letGoEnquiry(l, reason, () => { if (gen === routeGen && onNewJobsRoute()) void load() })
  refreshListSafely()
}

// What has happened, newest first: the note lines the sends, replies and calls left, then the day the enquiry came in.
const WAY_WORDS: Record<Kept['way'], string> = { whatsapp: 'a WhatsApp', text: 'a text', email: 'an email', call: 'a call', form: 'the website form' }
function happenedLines(l: Lead, kept: Kept[] | null): [string, string][] {
  const out: [string, string][] = []
  // Each send says once (tidy-up item 6, 20 Oct 2026): a message a send kept is matched to the note line that same send
  // wrote, by its key (the server's `counted`), not by its day. One kept with words is said, and its line is not; one
  // with no words (sent some other way) is said by its line ("You sent a WhatsApp (message 2)."). Lines no kept message
  // claims are said too, unless messages from before that matching are kept, which, as before, already say them.
  const claimed: string[] = []
  const said: string[] = []
  const skip = new Set<number>()
  for (const m of kept ?? []) {
    if (!m.action || !m.counted) continue
    if (m.body) claimed.push(m.counted)
    else { skip.add(m.id); said.push(m.counted) }
  }
  const unmatchedOld = (kept ?? []).some((m) => m.direction === 'out' && !m.action)
  // With the conversation kept (0058): every message in their words, newest first. The emailed replies it also holds
  // are not said twice.
  if (kept && kept.length) {
    for (const m of kept.slice().reverse()) {
      if (skip.has(m.id)) continue
      const when = shortDate(m.at.slice(0, 10))
      const words = m.body ? `: “${messageExcerpt(m.body, 90)}”` : '.'
      if (m.direction === 'in') out.push([when, m.way === 'form' ? `Their website form${words}` : `They sent ${WAY_WORDS[m.way]}${words}`])
      // A quote given on the phone, kept by I sent a quote on Messages (25 Oct 2026): a call, not one "opened to send".
      else if (m.way === 'call') out.push([when, `A call from you${words}`])
      else out.push([when, `You ${m.state === 'opened' ? 'opened' : 'sent'} ${WAY_WORDS[m.way]}${m.state === 'opened' ? ' to send' : ''}${m.state === 'failed' ? ', and it failed' : m.state === 'delivered' ? ', delivered' : ''}${words}`])
    }
  }
  for (const raw of (l.contact_note ?? '').split('\n').map((s) => s.trim()).filter(Boolean).reverse()) {
    const at = claimed.indexOf(raw)
    if (at >= 0) { claimed.splice(at, 1); continue }
    const own = said.indexOf(raw)
    if (own >= 0) said.splice(own, 1)
    else if (unmatchedOld && /: enquiry (WhatsApp|text|email) \d+$/.test(raw)) continue
    const m = /^(\d{1,2} [A-Z][a-z]{2}): (.+)$/.exec(raw)
    if (!m) { out.push(['', raw]); continue }
    const what = m[2]!
    const sent = /^enquiry (WhatsApp|text|email) (\d+)$/.exec(what)
    const last = /^last try (WhatsApp|text|email)$/.exec(what)
    out.push([noteWords(m[1]!), sent ? `You sent ${sent[1] === 'email' ? 'an email' : sent[1] === 'text' ? 'a text' : 'a WhatsApp'}${sent[2] === '1' ? '' : ` (message ${sent[2]})`}.`
      : last ? `One last try by ${last[1]}.` : what === 'they replied' ? 'They replied.' : what === 'quoted' ? 'Marked as quoted.' : noteWords(`${what[0]!.toUpperCase()}${what.slice(1)}`)])
  }
  for (const r of quoteReplies(l).slice().reverse()) {
    if (kept?.some((m) => m.direction === 'in' && m.body === r.body)) continue
    out.push([replyWhen(r), `They ${r.subject === 'Text' ? 'texted' : r.subject === 'WhatsApp' ? 'sent a WhatsApp' : 'emailed'}: “${messageExcerpt(r.body, 80)}”`])
  }
  if (l.quoted_on) out.push([shortDate(l.quoted_on), l.came_by === 'email' ? 'Their enquiry came in by email.' : 'Enquiry noted.'])
  return out
}

// One person's page.
function enquiryPage(l: Lead, lane: EnquiryLane, today: string, phone: boolean): HTMLElement {
  const page = el('section', { class: `nj-detail nj-detail-${lane}`, 'aria-label': l.name, 'data-lead-id': String(l.id) })
  if (phone) {
    const back = el('button', { type: 'button', class: 'nj-back' }, '‹ New jobs')
    back.addEventListener('click', () => openEnquiry(null))
    page.append(back)
  }
  const pill = lanePill(l, lane, today)
  page.append(el('div', { class: 'nj-detail-top' },
    el('div', {}, el('h3', { tabindex: '-1' }, l.name), el('p', { class: 'nj-detail-where' }, [whereLine(l), l.address && !whereLine(l).includes(l.address) ? l.address : ''].filter(Boolean).join(' · '))),
    pill ? el('span', { class: `nj-pill nj-pill-${pill.tone}` }, pill.text) : ''))

  // Their message: tap to open or hide (Liam, 10 Oct 2026: "Should be able to tap it i guess to see and hide it?").
  const words = enquiryWords(l)
  if (words.message) {
    const full = messageShown.has(l.id)
    const short = messageExcerpt(words.message, 140)
    const cut = short !== words.message.replace(/\s+/g, ' ').trim()
    const how = l.came_by === 'email' ? (words.details.some(([k]) => k === 'From') ? 'website form' : 'email') : 'enquiry'
    const label = el('span', { class: 'nj-said-label' }, `Their message · ${how}${l.quoted_on ? `, ${shortDate(l.quoted_on)}` : ''}`)
    const text = el('span', { class: 'nj-said-text' }, full || !cut ? words.message : short!)
    if (cut) {
      const box = el('button', { type: 'button', class: 'nj-said', 'aria-expanded': String(full) }, label, text, el('span', { class: 'nj-said-toggle' }, full ? 'Hide' : 'Show all'))
      box.addEventListener('click', () => {
        if (full) messageShown.delete(l.id)
        else messageShown.add(l.id)
        renderCards()
        document.querySelector<HTMLElement>('.nj-detail .nj-said')?.focus({ preventScroll: true })
      })
      page.append(box)
    } else page.append(el('div', { class: 'nj-said' }, label, text))
  } else if (l.came_by === 'email') page.append(el('p', { class: 'nj-said nj-said-none' }, 'No message, only the form.'))
  const replies = replyBlock(l)
  if (replies) page.append(replies)

  const convo = conversationOf(l)
  const ways = waysRow(l, lane, true, convo?.sending)
  const ready = wordsToSend(l, lane)
  // The words first, then the ways to send them: change it, then tap (Liam, 10 Oct 2026).
  if (!(ready && leadIsOpen(l))) page.append(ways)
  if (ready && leadIsOpen(l)) {
    // Editable before sending: WhatsApp, Text and Email open with what the box says.
    page.append(el('div', { class: 'nj-ready' },
      el('span', { class: 'nj-said-label' }, lane === 'chase' ? 'Your nudge, ready (change it if you like)' : 'Your first reply, ready (change it if you like)'),
      readyBox(readyKeyFor(l, lane), usualWords(l, lane) ?? ready, lane === 'chase' ? `Your nudge to ${l.name}` : `Your first reply to ${l.name}`),
      el('p', { class: 'nj-ready-help' }, lane === 'chase'
        ? 'WhatsApp, Text or Email opens with this written; press send there. It is the last message: no answer a week later and they move to Maybe later.'
        : 'WhatsApp, Text or Email opens with this written; press send there. Or write them a price straight away.')))
    page.append(ways)
  }
  if (leadIsOpen(l) && !quoted(l) && !l.converted_customer_id) page.append(quoteButton(l, `Write a quote for ${l.name}`, 'nj-quote-big'))
  if (l.converted_customer_id && l.has_extra_offer) {
    const add = el('button', { type: 'button', class: 'link-button' }, 'Add accepted extras to their visit')
    add.addEventListener('click', () => void bookEnquiry(l))
    page.append(el('div', { class: 'notice enquiry-pending-offer' }, el('p', {}, "Offered extras weren't added: open the enquiry to add them"), add))
  }
  if (quoted(l)) {
    const priced = quotedWords(l, 'long')
    if (priced) page.append(el('p', { class: 'nj-quoted' }, `Quoted ${priced}.`))
  }
  if (lane === 'waiting') {
    const n = messagesSent(l)
    const due = n === 1 ? nudgeDueOn(l) : n >= 2 ? letGoOn(l) : null
    if (due) page.append(el('p', { class: 'nj-next' }, n === 1 ? `No answer by ${dayWords(due)}? They move to To chase, with a nudge ready.` : `The nudge has gone. No answer by ${dayWords(due)}? They move to Maybe later.`))
  }
  if (lane === 'letgo') page.append(el('p', { class: 'nj-next' }, 'Let go: two messages and no answer. A reply, or a message from you, brings them straight back.'))

  // The facts, as the enquiry gave them.
  const facts = el('dl', { class: 'nj-facts' })
  let factCount = 0
  const fact = (k: string, v: string | null | undefined): void => { if (v && v.trim()) { factCount++; facts.append(el('div', {}, el('dt', {}, k), el('dd', {}, v))) } }
  fact('Phone', l.phone)
  fact('Email', l.email)
  for (const [k, v] of words.details) if (k !== 'From') fact(postcodeWords(k), v)
  fact('Found you', words.details.find(([k]) => k === 'From')?.[1].replace(/^the website's quote form/, 'Website form') ?? words.note)
  fact('Notes', l.notes)
  fact('Notes 2', l.notes2)
  if (factCount) page.append(facts)

  const happened = happenedLines(l, convo?.messages ?? null)
  if (happened.length) {
    const list = el('ol', { class: 'nj-happened', 'aria-label': 'What has happened' })
    for (const [when, what] of happened) list.append(el('li', {}, when ? el('span', { class: 'nj-happened-when' }, when) : '', el('span', {}, what)))
    page.append(el('h4', { class: 'nj-detail-h' }, "What's happened"), list)
  }

  // What happens next.
  const outcomes = el('div', { class: 'nj-outcomes' })
  let why: HTMLElement | null = null
  if (leadIsOpen(l)) {
    // Book them in (19 Oct 2026), the same as on their Messages card: won, a customer and the visit in one save.
    const who = firstName(l.name) || l.name
    const book = el('button', { type: 'button', class: 'nj-outcome nj-outcome-book' }, bookingWaiting(l) ? `Booking ${who}…` : `Book ${who}`) as HTMLButtonElement
    book.disabled = leadBusy(l.id) || bookingWaiting(l)
    book.addEventListener('click', () => void bookEnquiry(l))
    outcomes.append(book)
    if (messagesSent(l) > 0 || lane === 'letgo') {
      const replied = el('button', { type: 'button', class: 'nj-outcome', 'aria-label': `They replied: ${l.name}` }, 'They replied')
      replied.disabled = leadBusy(l.id)
      replied.addEventListener('click', () => theyReplied(l, replied))
      outcomes.append(replied)
    }
    const move = (label: string, status: string, aria: string): void => {
      const b = el('button', { type: 'button', class: 'nj-outcome', 'aria-label': `${aria}: ${l.name}` }, label)
      b.disabled = leadBusy(l.id)
      b.addEventListener('click', () => { if (!leadBusy(l.id)) { commitStatus(l, status); refreshListSafely() } })
      outcomes.append(b)
    }
    move('They said yes', 'CONFIRMED', 'Won')
    if (leadTone(l) !== 'maybe') move('Maybe later', 'MAYBE', 'Maybe later')
    move('Not going ahead', 'DECLINED', 'Not going ahead')
    // Remove: the swipe's three reasons, for the computer and for anyone who would rather tap (src/let-go.ts).
    const remove = el('button', { type: 'button', class: 'nj-outcome nj-outcome-quiet', 'aria-expanded': 'false', 'aria-label': `Remove ${l.name}` }, 'Remove') as HTMLButtonElement
    remove.disabled = leadBusy(l.id)
    why = el('div', { class: 'nj-letgo-page', hidden: '' }, el('p', { class: 'muted' }, 'Why remove them?'), reasonButtons(l.name, (reason) => letGoLead(l, reason), { whatsapp: byWhatsApp(l) }))
    const box = why
    remove.addEventListener('click', () => { const show = box.hidden; box.hidden = !show; remove.setAttribute('aria-expanded', String(show)) })
    outcomes.append(remove)
  }
  outcomes.append(...customerLinkControls(l, true))
  // Chat (0060): every message with them in Messages' Chats.
  outcomes.append(el('a', { class: 'nj-outcome nj-outcome-quiet', href: `#/chats?lead=${l.id}`, 'aria-label': `Chat with ${l.name}` }, 'Chat'))
  const edit = el('button', { type: 'button', class: 'nj-outcome nj-outcome-quiet', 'data-edit-lead': String(l.id), 'aria-label': `Edit job for ${l.name}` }, 'Edit details')
  if (leadBusy(l.id)) { edit.disabled = true; edit.title = 'Still saving. One edit at a time.' } else edit.addEventListener('click', () => openDialog(l, edit))
  outcomes.append(edit)
  page.append(outcomes)
  if (why) page.append(why)
  // Every stored status stays one pick away (Quote, NOT HOME, a wording from the sheet).
  page.append(el('div', { class: 'nj-status-row' }, el('span', { class: 'muted' }, 'Status'), statusControl(l)))
  return page
}

// The lists. Kept under the old name: the full sheet's redraw calls it.
function renderCards(): void {
  const container = document.querySelector<HTMLElement>('#newjobs-cards')
  if (!container) return
  const today = todayISO()
  const rows = rowsToShow()
  const by = new Map<EnquiryLane, Lead[]>()
  for (const l of rows) {
    const lane = enquiryLane(l, today)
    if (!by.has(lane)) by.set(lane, [])
    by.get(lane)!.push(l)
  }
  const lane = (k: EnquiryLane): Lead[] => by.get(k) ?? []
  const oldestFirst = (a: string | null | undefined, b: string | null | undefined): number => (a ?? '9999-12-31').localeCompare(b ?? '9999-12-31')
  // A reply waiting first, then the longest waiting.
  lane('answer').sort((a, b) => Number((b.replies_unseen ?? 0) > 0) - Number((a.replies_unseen ?? 0) > 0) || oldestFirst(a.quoted_on, b.quoted_on) || a.id - b.id)
  lane('chase').sort((a, b) => oldestFirst(a.enquiry_messaged_on, b.enquiry_messaged_on) || a.id - b.id)
  lane('waiting').sort((a, b) => oldestFirst(nudgeDueOn(a) && messagesSent(a) === 1 ? nudgeDueOn(a) : letGoOn(a) ?? a.quoted_on, nudgeDueOn(b) && messagesSent(b) === 1 ? nudgeDueOn(b) : letGoOn(b) ?? b.quoted_on) || a.id - b.id)
  for (const k of ['later', 'letgo', 'won', 'closed', 'sorting'] as EnquiryLane[]) lane(k).sort((a, b) => (b.quoted_on ?? '').localeCompare(a.quoted_on ?? '') || b.id - a.id)

  const phone = phoneLayout()
  if (revealLead !== null && leads.has(revealLead)) { openLead = revealLead; openClosedByHand = false }
  revealLead = null
  if (openLead !== null && !leads.has(openLead)) openLead = null
  // On a computer someone is always open: the first to answer, else the first to chase.
  if (!phone && openLead === null && !openClosedByHand) openLead = (lane('answer')[0] ?? lane('chase')[0])?.id ?? null
  const showPage = openLead !== null
  // On a phone their page takes the whole screen; the list, search and heading come back with "‹ New jobs".
  // (display, not hidden: the page heading's own display rule would win over the hidden attribute.)
  for (const node of [document.querySelector<HTMLElement>('#newjobs-heading'), document.querySelector<HTMLElement>('#newjobs-bar'), alertsRow]) {
    if (node) node.style.display = phone && showPage ? 'none' : ''
  }

  clear(container)
  const layout = el('div', { class: `nj-layout${showPage ? ' nj-layout-open' : ''}${phone ? ' nj-layout-phone' : ''}` })
  const list = el('div', { class: 'nj-list' })
  const answer = lane('answer'), chase = lane('chase'), waiting = lane('waiting')
  list.append(el('div', { class: 'nj-summary', role: 'status' },
    el('span', { class: 'nj-chip nj-chip-answer' }, `${answer.length} to answer`),
    el('span', { class: 'nj-chip nj-chip-chase' }, `${chase.length} to chase`),
    el('span', { class: `nj-chip nj-chip-waiting${landedLead !== null && waiting.some((l) => l.id === landedLead) ? ' nj-chip-bump' : ''}` }, `${waiting.length} waiting on them`)))

  if (!answer.length && !chase.length && !filters.search.trim()) {
    // Everyone has an answer: said plainly, with when the next chase comes.
    const next = waiting.map((l) => ({ l, on: messagesSent(l) === 1 ? nudgeDueOn(l) : null })).filter((x) => x.on).sort((a, b) => a.on!.localeCompare(b.on!))[0]
    list.append(el('section', { class: 'nj-alldone' },
      el('span', { class: 'nj-alldone-tick', 'aria-hidden': 'true' }),
      el('h3', {}, 'Everyone has an answer'),
      el('p', {}, next ? `Next to chase: ${next.l.name} on ${dayWords(next.on!)}.` : 'Nobody is waiting on you.')))
  }
  const section = (k: EnquiryLane, sub: string): void => {
    const people = lane(k)
    if (!people.length) return
    const sec = el('section', { class: `nj-lane nj-lane-${k}`, 'aria-label': LANE_TITLE[k] })
    sec.append(el('div', { class: 'nj-lane-head' }, el('h3', {}, `${LANE_TITLE[k]} · ${people.length}`), el('span', { class: 'muted' }, sub)))
    for (const l of people) sec.append(laneCard(l, k, today))
    list.append(sec)
  }
  section('answer', 'longest waiting first')
  section('chase', 'quoted or asked, no answer yet')

  // Everyone else, folded under their stage. Let go sits with Maybe later.
  const folds = el('div', { class: 'nj-folds' })
  const fold = (key: string, title: string, people: Lead[], lanesOf: (l: Lead) => EnquiryLane): void => {
    if (!people.length) return
    const d = el('details', { class: `nj-fold nj-fold-${key}` }, el('summary', {}, el('span', {}, title), el('span', { class: 'nj-fold-count' }, String(people.length))))
    d.open = foldsOpen.has(key) || people.some((l) => l.id === openLead) || !!filters.search.trim()
    d.addEventListener('toggle', () => { if (d.open) foldsOpen.add(key); else foldsOpen.delete(key) })
    for (const l of people) {
      const k = lanesOf(l)
      const pill = lanePill(l, k, today)
      const row = el('button', { type: 'button', class: `nj-row${openLead === l.id ? ' nj-row-on' : ''}`, 'data-lead-id': String(l.id), 'aria-label': `Open ${l.name}` },
        el('span', { class: 'nj-row-name' }, l.name), el('span', { class: 'nj-row-where' }, whereLine(l)), pill ? el('span', { class: `nj-pill nj-pill-${pill.tone}` }, pill.text) : '')
      row.addEventListener('click', () => openEnquiry(l.id))
      d.append(row)
    }
    folds.append(d)
  }
  fold('waiting', 'Waiting on them', waiting, () => 'waiting')
  fold('later', 'Maybe later', [...lane('later'), ...lane('letgo')], (l) => enquiryLane(l, today))
  fold('won', 'Won, now customers', lane('won'), () => 'won')
  fold('closed', 'Not going ahead', lane('closed'), () => 'closed')
  fold('sorting', 'Needs sorting', lane('sorting'), () => 'sorting')
  list.append(folds)
  const sheet = el('button', { type: 'button', class: 'link-button nj-sheet-link' }, 'Open the full sheet')
  sheet.addEventListener('click', () => { fullSheet = true; render([...leads.values()], document.querySelector<HTMLElement>('#screen')!) })
  list.append(sheet)
  layout.append(list)

  const open = openLead !== null ? leads.get(openLead) ?? null : null
  if (open) layout.append(enquiryPage(open, enquiryLane(open, today), today, phone))
  else if (!phone) layout.append(el('section', { class: 'nj-detail nj-detail-empty' }, el('p', { class: 'muted' }, 'Choose someone to see their enquiry.')))
  container.append(layout)

  if (landedLead !== null) {
    const card = container.querySelector<HTMLElement>(`[data-lead-id="${landedLead}"]`)
    if (card) landFlash(card)
  }
  if (lastMovedLead !== null && !leadBusy(lastMovedLead)) lastMovedLead = null
}

// Their replies to the quote they were emailed (0055; Liam, 9 Oct 2026: replies "land straight in roundly app"): the
// newest in full, the earlier ones folded under it, and Seen while it is new (Needs you lists it until then).
function replyBlock(l: Lead): HTMLElement | null {
  const replies = quoteReplies(l)
  const last = replies.at(-1)
  if (!last) return null
  const fresh = (l.replies_unseen ?? 0) > 0
  const box = el('section', { class: `newjobs-reply${fresh ? ' newjobs-reply-new' : ''}`, 'aria-label': `Reply from ${l.name}` },
    el('strong', {}, `${fresh ? 'New reply' : 'Replied'} · ${replyWhen(last)}`),
    el('p', { class: 'newjobs-reply-text' }, last.body))
  if (replies.length > 1) {
    const earlier = el('details', { class: 'newjobs-reply-earlier' }, el('summary', {}, `Earlier replies (${replies.length - 1})`))
    for (const r of replies.slice(0, -1).reverse()) earlier.append(el('p', { class: 'newjobs-reply-text' }, el('span', { class: 'muted' }, `${replyWhen(r)}: `), r.body))
    box.append(earlier)
  }
  if (fresh) {
    const seen = el('button', { type: 'button', class: 'btn-secondary' }, 'Seen')
    seen.addEventListener('click', () => markRepliesSeen(l, seen, () => refreshListSafely()))
    box.append(seen)
  }
  return box
}

function renderCount(): void {
  const node = document.querySelector<HTMLElement>('#newjobs-count')
  if (node) node.textContent = `${rowsToShow().length} of ${leads.size} jobs`
}

// ---- add / edit dialog -------------------------------------------------------

// The 12 stored values a dialog diffs its draft against: only fields the user
// actually changed are PATCHed, so a conflict retry can never silently
// overwrite the other owner's unrelated edits with stale copies.
function baseValuesOf(l: Lead | null): Record<string, unknown> {
  if (!l) return {}
  return {
    quoted_on: l.quoted_on,
    name: l.name,
    address: l.address,
    phone: l.phone,
    email: l.email,
    initial_pence: l.initial_pence,
    regular_pence: l.regular_pence,
    cycle_weeks: l.cycle_weeks,
    status_raw: l.status_raw,
    initial_lead: l.initial_lead,
    notes: l.notes,
    checked: l.checked,
    notes2: l.notes2,
  }
}

function openDialog(lead: Lead | null, trigger: HTMLElement): void {
  if (editing || (lead ? leadBusy(lead.id) : addBlocked())) return
  editing = true
  const isNew = lead === null
  const dialogGen = routeGen // callbacks from a cancelled dialog must never touch a newer one
  const path = isNew ? '/api/leads' : `/api/leads/${lead!.id}`

  const dlg = el('dialog', { class: 'newjobs-dialog', 'aria-label': isNew ? 'Add new job' : 'Edit job' })
  const form = el('form', { novalidate: 'novalidate' })
  const title = el('h3', {}, isNew ? 'Add new job' : `Edit: ${lead!.name}`)
  const err = el('p', { class: 'form-error', role: 'alert' })

  const fields = el('div', { class: 'booking-fields' })
  const input = (id: string, label: string, attrs: Record<string, string> = {}, value: string): HTMLInputElement => {
    const inp = el('input', { id, 'aria-label': label, ...attrs })
    inp.value = value
    return inp
  }
  const labeled = (inp: HTMLElement, label: string, help?: HTMLElement): HTMLElement => {
    const wrap = el('label', { class: 'booking-field' }, label)
    wrap.append(inp)
    if (help) wrap.append(help)
    return wrap
  }
  const text = (v: unknown): string => (v === null || v === undefined ? '' : String(v))
  const moneyText = (v: unknown): string => (typeof v === 'number' ? (v / 100).toFixed(2) : '')

  const name = input('nj-name', 'Customer name (required)', { required: 'required' }, text(lead?.name))
  const quotedOn = input('nj-date', 'Date', { type: 'date' }, text(lead?.quoted_on))
  const address = input('nj-address', 'Address', {}, text(lead?.address))
  const phone = input('nj-phone', 'Phone', { type: 'tel' }, text(lead?.phone))
  const email = input('nj-email', 'Email', { type: 'email' }, text(lead?.email))
  const initial = input('nj-initial', `Initial price in ${currencyWord()}`, { inputmode: 'decimal', placeholder: 'e.g. 45 or 45.00' }, moneyText(lead?.initial_pence))
  const regular = input('nj-regular', `Regular price in ${currencyWord()}`, { inputmode: 'decimal', placeholder: 'e.g. 25 or 25.00' }, moneyText(lead?.regular_pence))
  const cycle = input('nj-cycle', 'Cycle in weeks', { inputmode: 'decimal', placeholder: 'e.g. 8 or 2.5' }, text(lead?.cycle_weeks))
  // A fixed list, not free text: typed wordings were how the near-duplicates arrived (28 Sep 2026).
  const statusRaw = el('select', { id: 'nj-status', 'aria-label': 'Status' })
  statusRaw.append(el('option', { value: '' }, statusWords('')))
  for (const o of statusOptions(lead?.status_raw)) statusRaw.append(el('option', { value: o }, statusWords(o)))
  statusRaw.value = statusSelectValue(lead?.status_raw)
  const statusHelp = el('p', { class: 'field-help' })
  const updateStatusHelp = (): void => {
    statusHelp.textContent = ''
    statusHelp.append('Goes in: ', el('strong', {}, statusColumnWords(statusRaw.value)))
  }
  updateStatusHelp() // every initial state gets exactly one preview, blank included
  statusRaw.addEventListener('change', updateStatusHelp)
  const initialLead = el('textarea', { id: 'nj-lead', 'aria-label': 'Initial lead', rows: '4' })
  initialLead.value = text(lead?.initial_lead)
  const notes = el('textarea', { id: 'nj-notes', 'aria-label': 'Notes', rows: '3' })
  notes.value = text(lead?.notes)
  const notes2 = el('textarea', { id: 'nj-notes2', 'aria-label': 'Notes 2', rows: '3' })
  notes2.value = text(lead?.notes2)
  const checked = el('input', { id: 'nj-checked', type: 'checkbox' })
  ;(checked as HTMLInputElement).checked = Boolean(lead?.checked)
  const checkedLabel = el('label', { class: 'check-label' }, checked, 'Checked')

  fields.append(
    labeled(quotedOn, 'Date'),
    labeled(name, 'Customer name'),
    labeled(address, 'Address'),
    labeled(phone, 'Phone'),
    labeled(email, 'Email'),
    labeled(initial, `Initial ${currencySymbol()}`),
    labeled(regular, `Regular ${currencySymbol()}`),
    labeled(cycle, 'Cycle (weeks)'),
    labeled(statusRaw, 'Status', statusHelp),
    labeled(initialLead, 'Initial lead'),
    labeled(notes, 'Notes'),
    checkedLabel,
    labeled(notes2, 'Notes 2'),
  )

  const save = el('button', { type: 'submit', class: 'btn-primary' }, isNew ? 'Add job' : 'Save')
  const cancel = el('button', { type: 'button' }, 'Cancel')
  const footer = el('div', { class: 'booking-footer' }, err, save, cancel)
  form.append(title, fields, footer)
  dlg.append(form)
  document.body.append(dlg)

  // The stored row as it was when the dialog OPENED — immutable for the
  // dialog's whole life. Every diff is taken against THIS, so a fetched
  // latest row can never make the other owner's edits look like ours.
  const baseRow = baseValuesOf(lead)
  let liveVersion = lead?.version ?? 0

  let submitting = false // synchronous double-submit guard, set before mutate
  let pendingKey: string | null = null // a queued attempt still in flight
  let conflictKey: string | null = null // parked conflict entry, dismissed only on explicit resolve
  let closed = false

  const showNote = (message: string): void => {
    err.textContent = message
  }
  // Conflict review. The tap fetches the latest row, shows WHICH fields the
  // other owner changed (measured against the opening baseline), and only
  // here — the user's explicit resolve — is the parked conflict dismissed
  // and Save handed back. The retry then sends just the fields the user
  // edited versus the opening baseline, at the fresh version: the other
  // owner's fields are never re-sent, so nothing of theirs is restamped.
  const showConflict = (): void => {
    err.textContent = ''
    err.append('Changed somewhere else first. Your edits are kept: ')
    const retry = el('button', { type: 'button', class: 'newjobs-retry-btn' }, 'load latest and review')
    retry.addEventListener('click', () => {
      if (closed || routeGen !== dialogGen || !dlg.isConnected) return
      retry.setAttribute('disabled', 'disabled')
      void api<{ lead: Lead }>(path)
        .then((d) => {
          if (closed || routeGen !== dialogGen || !dlg.isConnected) return
          liveVersion = d.lead.version
          const latest = baseValuesOf(d.lead)
          const changedByOther = Object.keys(latest).filter((k) => latest[k] !== baseRow[k])
          const labels: Record<string, string> = { quoted_on: 'Date', name: 'Customer',
            address: 'Address', phone: 'Phone', email: 'Email', initial_pence: 'Initial price', regular_pence: 'Regular price',
            cycle_weeks: 'Cycle', status_raw: 'Status', initial_lead: 'Initial lead', notes: 'Notes',
            checked: 'Checked', notes2: 'Notes 2' }
          const summary = changedByOther.map((k) => `${labels[k] ?? k}: ${
            k.endsWith('_pence') ? money(latest[k] as number | null)
              : k === 'checked' ? (latest[k] ? 'checked' : 'not checked')
                : String(latest[k] ?? '(blank)')}`).join('; ')
          if (conflictKey) {
            dismiss(conflictKey) // explicitly resolved — discard the parked entry
            conflictKey = null
          }
          pendingKey = null
          submitting = false
          save.removeAttribute('disabled')
          save.textContent = isNew ? 'Add job' : 'Save'
          showNote(
            changedByOther.length === 0
              ? 'Latest version loaded. Your edits are kept: check them, then Save.'
              : `The other owner's latest values: ${summary}. Saving applies only your edited fields. Check them, then Save.`,
          )
        })
        .catch((ex) => {
          if (closed || routeGen !== dialogGen || !dlg.isConnected) return
          retry.removeAttribute('disabled')
          showNote((ex as Error).message)
        })
    })
    err.append(retry)
  }

  // Close the dialog. `refresh` true after a save lands, or when data moved
  // underneath the editor, so the list on screen shows the truth. Every
  // action is scoped to THIS route generation — a stale close never touches
  // a newer screen, and load() itself double-checks the hash.
  const finish = (restoreFocus: boolean, refresh: boolean): void => {
    if (closed) return
    closed = true
    editing = false
    const pendingRefresh = refresh || refreshPending
    refreshPending = false
    dialogQueueChanged = null // detach the shared queue forwarder
    dlg.close()
    dlg.remove()
    if (restoreFocus && routeGen === dialogGen && onNewJobsRoute()) trigger.focus()
    if (routeGen === dialogGen && pendingRefresh && onNewJobsRoute()) {
      void load().then(() => {
        if (!restoreFocus || routeGen !== dialogGen || editing || !onNewJobsRoute()) return
        const matches = lead ? [...document.querySelectorAll<HTMLButtonElement>(`[data-edit-lead="${lead.id}"]`)] : []
        const target = matches.find((button) => button.getClientRects().length > 0)
          ?? document.querySelector<HTMLButtonElement>('[data-newjobs-add]')
        target?.focus({ preventScroll: true })
      })
    }
  }

  // Resolves attempts that land from the BACKGROUND retry pass (those have
  // no callback): the entry leaves the queue = saved, close and refresh;
  // parked conflict or failed = surfaced here with the draft intact.
  dialogQueueChanged = () => {
    if (closed || routeGen !== dialogGen || !dlg.isConnected || !pendingKey) return
    const entry = getQueue().find((m) => m.key === pendingKey)
    if (!entry) {
      pendingKey = null
      finish(true, true)
      return
    }
    if (entry.status === 'conflict') {
      pendingKey = null
      conflictKey = entry.key
      showConflict()
    } else if (entry.status === 'failed') {
      pendingKey = null
      submitting = false
      save.removeAttribute('disabled')
      save.textContent = isNew ? 'Add job' : 'Save'
      showNote(`The server rejected this on retry: ${entry.error ?? 'unknown reason'} (dismiss it in the corner list).`)
    }
  }

  cancel.addEventListener('click', () => finish(true, false))
  dlg.addEventListener('cancel', (e) => {
    e.preventDefault()
    finish(true, false)
  })

  form.addEventListener('submit', (e) => {
    e.preventDefault()
    if (submitting || conflictKey) return // synchronous guard — never two attempts at once
    err.textContent = ''
    let full: Record<string, unknown>
    try {
      full = {
        quoted_on: quotedOn.value || null,
        name: name.value.trim(),
        address: address.value.trim() || null,
        phone: phone.value.trim() || null,
        email: email.value.trim() || null,
        initial_pence: parseMoney(initial.value),
        regular_pence: parseMoney(regular.value),
        cycle_weeks: parseCycleWeeks(cycle.value),
        status_raw: statusRaw.value.trim() || null,
        initial_lead: initialLead.value.trim() || null,
        notes: notes.value.trim() || null,
        checked: checked.checked ? 1 : 0,
        notes2: notes2.value.trim() || null,
      }
    } catch (ex) {
      err.textContent = (ex as Error).message
      return
    }
    if (!full.name) {
      err.textContent = 'Customer name is required'
      return
    }
    // Diff against the OPENING baseline: only what the user actually changed
    // is sent, so a post-conflict retry can never restamp the other owner's
    // unrelated fields with stale copies.
    const changed: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(full)) {
      if (baseRow[k] !== v) changed[k] = v
    }
    if (!isNew && Object.keys(changed).length === 0) {
      showNote('Nothing changed.')
      return
    }
    submitting = true // set before mutate; held while sending or queued
    save.setAttribute('disabled', 'disabled')
    save.textContent = 'saving…'
    // With what the dialog opened on for each box changed (same-time saves): it saves unless someone changed one of
    // those same boxes first.
    const body = isNew ? full : { ...changed, version: liveVersion, base: Object.fromEntries(Object.keys(changed).map((k) => [k, baseRow[k] ?? null])) }
    void mutate({
      label: `${isNew ? 'Add job' : 'Edit job'}: ${String(full.name)}`,
      method: isNew ? 'POST' : 'PATCH',
      path,
      body,
      onAck: () => {
        // Success closes THIS dialog only. A dialog already closed (or left
        // behind by navigation) does nothing here — the open screen's
        // data-changed listener owns any refresh.
        if (!closed && routeGen === dialogGen && dlg.isConnected) finish(true, true)
      },
      onQueued: (m) => {
        pendingKey = m.key // offline/5xx — save stays blocked until the queue resolves it
        if (!closed && routeGen === dialogGen && dlg.isConnected) {
          save.textContent = 'Waiting for connection…'
          cancel.textContent = 'Close'
          showNote('Saved on this device for retry. You can close this panel; the change will send when connected.')
        }
      },
    }).then((ack: Ack) => {
      if (ack.ok) return // onAck handled it
      if (closed || routeGen !== dialogGen || !dlg.isConnected) return
      if (ack.status === 409) {
        if (isNew) {
          // a create 409s only on key reuse — nothing to resolve
          submitting = false
          save.removeAttribute('disabled')
          save.textContent = 'Add job'
          showNote('That add already went through. Check the list before trying again.')
          return
        }
        conflictKey = getQueue().find((m) => m.status === 'conflict' && m.path === path)?.key ?? null
        pendingKey = null
        showConflict() // save stays blocked until the explicit resolve/retry
        return
      }
      // Still in flight (offline/5xx — mutate parked it): the shared queue
      // forwarder resolves the outcome later. Hold the guard.
      if (pendingKey && getQueue().some((m) => m.key === pendingKey)) return
      // 4xx/422: invalid edit, never queued — draft stays, save back.
      submitting = false
      save.removeAttribute('disabled')
      save.textContent = isNew ? 'Add job' : 'Save'
      err.textContent = ack.error ?? 'rejected'
    })
  })

  dlg.showModal()
  name.focus()
}

// ---- convert dialog ----------------------------------------------------------

// Explicit conversion of one confirmed lead: link an existing customer
// (select only — their data is never touched, never overwritten) or create
// one from the lead's own values (review only — to change a value, edit the
// lead first and come back; nothing is retyped and nothing invented).
// Possible matches are surfaced by name/phone/address but NEVER auto-linked.
// The optional first booking is a separate step, offered only after the
// converted row has been saved.
//
// Exported for the diary's New-enquiries placement (schedule.ts): a dropped
// enquiry WITH a match candidate opens this same dialog instead of
// converting blind — the brief forbids a second copy of the choose /
// conflict / replay machinery here. It must be handed an ALREADY-CONFIRMED
// lead (the server refuses to convert anything else); the diary confirms
// first and passes the ack's fresh row.
export function openConvert(lead: Lead, trigger: HTMLElement): void {
  if (editing || leadBusy(lead.id)) return
  editing = true
  const dialogGen = routeGen // callbacks from a cancelled dialog must never touch a newer one
  const path = `/api/leads/${lead.id}/convert`

  const dlg = el('dialog', { class: 'newjobs-dialog newjobs-convert-dialog', 'aria-label': `Make ${lead.name} a customer` })
  const err = el('p', { class: 'form-error', role: 'alert' })
  const cancel = el('button', { type: 'button' }, 'Cancel')
  const content = el('div', { class: 'newjobs-convert-body' })

  let submitting = false // synchronous double-submit guard, set before mutate
  let pendingKey: string | null = null // a queued conversion still in flight
  let conflictKey: string | null = null // parked conflict entry, resolved only explicitly
  let closed = false
  const actionButtons: HTMLButtonElement[] = []

  const showNote = (message: string): void => {
    err.textContent = message
  }
  const lockActions = (): void => {
    for (const b of actionButtons) b.setAttribute('disabled', 'disabled')
  }
  const unlockActions = (): void => {
    for (const b of actionButtons) b.removeAttribute('disabled')
  }

  // lead summary — what is being converted, straight from the stored row
  content.append(
    el('h3', {}, `Make them a customer: ${lead.name}`),
    el('p', { class: 'muted' }, [lead.address, lead.phone, `Status: ${lead.status_raw || lead.status}`].filter(Boolean).join(' · ')),
  )

  // ---- possible matches (loaded once, searched locally) --------------------
  // Lewis, 14 Oct 2026, read "Link this customer" as joining two neighbours and linked Fiona's job onto Daisy: the words
  // now say it is for the same person only, and a neighbour or friend is a new customer.
  const matchesTitle = el('h4', {}, 'Already a customer?')
  const matchesNote = el('p', { class: 'field-help newjobs-match-note' }, 'Checking existing customers…')
  const search = el('input', { type: 'search', placeholder: postcodeWords('Name, address or postcode'), 'aria-label': 'Search existing customers' })
  const list = el('div', { class: 'customer-picker' })
  let customers: Customer[] | null = null

  const matchRow = (c: Customer, reason?: string): HTMLElement => {
    const row = el('div', { class: 'customer-choice' })
    row.append(el('strong', {}, c.name))
    row.append(el('span', {}, [addressLine(c.address, c.postcode, ' · '), c.status !== 'active' ? c.status : ''].filter(Boolean).join(' · ')))
    if (reason) row.append(el('span', { class: 'muted' }, reason))
    const link = el('button', { type: 'button', 'aria-label': `Yes, ${lead.name} is ${c.name}` }, 'Yes, this is them')
    link.addEventListener('click', () => startConvert({ mode: 'existing', customer: c }, c.name))
    actionButtons.push(link)
    row.append(link)
    return row
  }

  const drawMatches = (): void => {
    clear(list)
    if (!customers) return
    const q = search.value.trim().toLowerCase().replace(/\s/g, '')
    if (q === '') {
      matchesNote.textContent = 'Choose a customer here only if they are the same person. A neighbour or a friend is a separate customer. Roundly suggests customers with the same name, phone or address.'
      const matches = findCustomerMatches(lead, customers)
      if (matches.length === 0) {
        list.append(el('p', { class: 'muted' }, 'No matching customers. Search if you think they are already a customer, or make them a new customer below.'))
      }
      for (const m of matches) list.append(matchRow(m.customer, `matches: ${m.reasons.join(', ')}`))
      return
    }
    matchesNote.textContent = ''
    const hits = customers.filter((c) => [c.name, c.address, c.postcode].some((v) => (v ?? '').toLowerCase().replace(/\s/g, '').includes(q)))
    for (const c of hits.slice(0, 30)) list.append(matchRow(c))
    if (hits.length === 0) list.append(el('p', { class: 'muted' }, 'No customers match this search.'))
    if (hits.length > 30) list.append(el('p', { class: 'muted' }, 'Type to narrow the list.'))
  }

  const loadCustomers = (): void => {
    matchesNote.textContent = 'Checking existing customers…'
    clear(list)
    void api<{ customers: Customer[] }>('/api/customers')
      .then((d) => {
        if (closed || routeGen !== dialogGen || !dlg.isConnected) return
        customers = d.customers
        drawMatches()
      })
      .catch((ex) => {
        if (closed || routeGen !== dialogGen || !dlg.isConnected) return
        matchesNote.textContent = ''
        matchesNote.append(
          `Couldn't check for matches (${(ex as Error).message}). A new customer could still be a duplicate. `,
        )
        const retry = el('button', { type: 'button' }, 'Try again')
        retry.addEventListener('click', () => loadCustomers())
        matchesNote.append(retry)
      })
  }
  search.addEventListener('input', drawMatches)

  // ---- the create-from-lead draft (review only) -----------------------------
  const draft = conversionDraft(lead)
  const draftRow = (label: string, value: string): HTMLElement =>
    el('li', {}, el('span', { class: 'muted' }, `${label}: `), value)
  // The postcode is the one thing typed here (bug hunt 8 Oct 2026: enquiries have none, so new customers had none).
  // Offered from the end of the address on a UK round, for the owner to check; the address shown is the one saved.
  const postcodeBox = el('input', { type: 'text', maxlength: '20', autocomplete: 'off', 'aria-label': postcodeWords('Postcode') })
  postcodeBox.value = postcodeAtEnd(draft.address, roundConfig().country) ?? ''
  const addressText = el('span', {})
  const showAddress = (): void => { addressText.textContent = addressWithoutPostcode(draft.address, postcodeBox.value.trim()) ?? 'none' }
  postcodeBox.addEventListener('input', showAddress)
  showAddress()
  const draftList = el(
    'ul',
    { class: 'newjobs-convert-draft' },
    draftRow('Name', draft.name),
    el('li', {}, el('span', { class: 'muted' }, 'Address: '), addressText),
    draftRow('Phone', draft.phone ?? '—'),
    draftRow('Origin (from initial lead)', draft.origin ?? '—'),
    draftRow('Notes', draft.notes ?? '—'),
    draftRow('First clean price', draft.first_clean_price_pence === null ? 'not priced yet' : money(draft.first_clean_price_pence)),
    draftRow('Regular price', draft.reg_price_pence === null ? 'not priced yet' : money(draft.reg_price_pence)),
    // No cycle of its own: the round's usual Every from sign-up, which is what the server saves (0073; Codex, 15 Oct 2026).
    draftRow('Cycle', cycleDaysFromWeeks(lead.cycle_weeks) == null && getMe()?.defaultCycleDays ? `${cycleLabel(getMe()!.defaultCycleDays!)}, your usual` : cycleProposalText(lead.cycle_weeks)),
  )
  const postcodeField = el('label', { class: 'booking-field newjobs-convert-postcode' }, el('span', {}, postcodeWords('Postcode')), postcodeBox,
    el('p', { class: 'muted field-help' }, postcodeBox.value ? 'Found at the end of the address. Check it, or change it.' : 'Type it in if you have it, so they show on the map.'))
  const create = el('button', { type: 'button', class: 'btn-primary' }, 'Create customer')
  create.addEventListener('click', () => startConvert({ mode: 'create', postcode: postcodeBox.value }, lead.name))
  actionButtons.push(create)
  content.append(
    matchesTitle,
    matchesNote,
    search,
    list,
    el('h4', {}, 'Or make them a new customer'),
    el(
      'p',
      { class: 'field-help' },
      postcodeWords(`Values come from this lead. To change them, close and edit the lead first, then convert. No price is invented${getMe()?.defaultCycleDays ? ', and with no cycle of its own they start on your usual one' : ' and no cycle'}; only the postcode is checked here.`),
    ),
    draftList,
    postcodeField,
    create,
  )

  // ---- outcome panels --------------------------------------------------------
  const showConflict = (): void => {
    err.textContent = ''
    err.append('Not saved: the lead changed somewhere else first (an edit, or it is already a customer). ')
    const reload = el('button', { type: 'button', class: 'newjobs-retry-btn' }, 'reload and review')
    reload.addEventListener('click', () => {
      if (closed || routeGen !== dialogGen || !dlg.isConnected) return
      if (conflictKey) {
        dismiss(conflictKey) // explicitly resolved by this reload
        conflictKey = null
      }
      finish(true, true)
    })
    err.append(reload)
  }

  // The saved state: the link is committed, booking is offered — never pushed.
  const showConverted = (choice: ConvertChoice, customerName: string, row: Lead): void => {
    clear(content)
    content.append(
      el('h3', {}, choice.mode === 'create' ? `${customerName} is now a customer` : `This job is now on ${customerName}'s page`),
      el('p', { class: 'muted' }, choice.mode === 'create'
        ? 'Book their first visit now, or later from their page.'
        : `Their messages show there too. Book a visit now, or later from their page. Not the same person? Tap Unlink on the job.`),
    )
    const done = el('button', { type: 'button' }, 'Close')
    done.addEventListener('click', () => finish(true, true))
    const actions = el('div', { class: 'booking-footer' })
    const cid = row.converted_customer_id
    if (typeof cid === 'number') {
      const book = el('button', { type: 'button', class: 'btn-primary' }, choice.mode === 'create' ? 'Book first visit' : 'Book a visit')
      book.addEventListener('click', () => {
        freshConversions.delete(cid) // offered once — the row shortcut goes with it
        finish(true, false)
        openBooking({ customerId: cid })
      })
      actions.append(book)
    }
    actions.append(done)
    content.append(actions)
    cancel.setAttribute('hidden', 'hidden')
  }

  const startConvert = (choice: ConvertChoice, customerName: string): void => {
    if (submitting || conflictKey || closed) return // never two attempts at once
    // synchronous re-check, not just the render-time state: an edit or
    // conversion for this row may have queued while the dialog was open.
    if (leadBusy(lead.id)) {
      showNote('This lead has a change still saving. Wait for it to finish, then try again.')
      return
    }
    submitting = true
    lockActions()
    err.textContent = ''
    void mutate({
      label: `${choice.mode === 'create' ? 'Convert (new customer)' : 'Convert (link customer)'}: ${lead.name}`,
      method: 'POST',
      path,
      body: convertBody(lead, choice),
      onAck: (ack) => {
        const fresh = ack.row as Lead
        if (typeof fresh.converted_customer_id === 'number') freshConversions.add(fresh.converted_customer_id)
        if (!closed && routeGen === dialogGen && dlg.isConnected) showConverted(choice, customerName, fresh)
      },
      onQueued: (m) => {
        pendingKey = m.key // offline/5xx — resolved by the queue (or the forwarder below)
        if (!closed && routeGen === dialogGen && dlg.isConnected) {
          cancel.textContent = 'Close'
          showNote('Saved on this device for retry. You can close this panel; the conversion will send when connected.')
        }
      },
    }).then((ack: Ack) => {
      if (ack.ok) return // onAck handled it
      if (closed || routeGen !== dialogGen || !dlg.isConnected) return
      if (ack.status === 409) {
        conflictKey = getQueue().find((m) => m.status === 'conflict' && m.path === path)?.key ?? null
        pendingKey = null
        showConflict() // stays blocked until the explicit reload-and-review
        return
      }
      // Still in flight (offline/5xx — mutate parked it): the queue forwarder
      // resolves the outcome later. Hold the guard.
      if (pendingKey && getQueue().some((m) => m.key === pendingKey)) return
      // 400/404/422: invalid request or state — never queued, panel stays.
      submitting = false
      unlockActions()
      err.textContent = ack.error ?? 'rejected'
    })
  }

  // Close the dialog (same contract as the edit dialog: route-scoped, queue
  // forwarder detached, refresh only when data moved).
  const finish = (restoreFocus: boolean, refresh: boolean): void => {
    if (closed) return
    closed = true
    editing = false
    const pendingRefresh = refresh || refreshPending
    refreshPending = false
    dialogQueueChanged = null
    dlg.close()
    dlg.remove()
    if (routeGen === dialogGen && pendingRefresh && onNewJobsRoute()) void load()
    else if (restoreFocus && routeGen === dialogGen && onNewJobsRoute()) trigger.focus()
  }

  // Resolves conversions that land from the BACKGROUND retry pass (no ack
  // callback on that path): entry gone = saved — close and reload, the row
  // gains its link and the fresh-conversion Book first visit shortcut;
  // conflict/failed = surfaced here, resolved explicitly.
  dialogQueueChanged = () => {
    if (closed || routeGen !== dialogGen || !dlg.isConnected || !pendingKey) return
    const entry = getQueue().find((m) => m.key === pendingKey)
    if (!entry) {
      pendingKey = null
      // Saved from the background pass — the user may be looking elsewhere;
      // say what just landed (the reload shows the link and the shortcut).
      toast(`Converted: ${lead.name}`)
      finish(true, true)
      return
    }
    if (entry.status === 'conflict') {
      pendingKey = null
      conflictKey = entry.key
      showConflict()
    } else if (entry.status === 'failed') {
      pendingKey = null
      showNote(`The server rejected this on retry: ${entry.error ?? 'unknown reason'} (dismiss it in the corner list).`)
    }
  }

  cancel.addEventListener('click', () => finish(true, false))
  dlg.addEventListener('cancel', (e) => {
    e.preventDefault()
    finish(true, false)
  })
  const footer = el('div', { class: 'booking-footer' }, err, cancel)
  dlg.append(content, footer)
  document.body.append(dlg)
  dlg.showModal()
  search.focus()
  loadCustomers()
}

// Unlink (Lewis, 14 Oct 2026): a new job made into the wrong customer goes back to being a new job. Only the job's link
// is cleared (POST /api/leads/:id/unlink); the customer's page stays exactly as it is.
function openUnlink(l: Lead, trigger: HTMLElement): void {
  if (editing || leadBusy(l.id)) return
  editing = true
  const gen = routeGen
  const who = l.customer_name ?? 'this customer'
  const Who = who.charAt(0).toUpperCase() + who.slice(1)
  const dlg = el('dialog', { class: 'newjobs-dialog newjobs-unlink-dialog', 'aria-label': `Unlink ${l.name}` }) as HTMLDialogElement
  const err = el('p', { class: 'form-error', role: 'alert' })
  const go = el('button', { type: 'button', class: 'btn-primary' }, 'Unlink') as HTMLButtonElement
  const cancel = el('button', { type: 'button' }, 'Cancel')
  let closed = false
  const finish = (refresh: boolean): void => {
    if (closed) return
    closed = true
    editing = false
    dlg.close()
    dlg.remove()
    if (gen !== routeGen || !onNewJobsRoute()) return
    if (refresh) refreshListSafely()
    else trigger.focus()
  }
  go.addEventListener('click', () => {
    if (leadBusy(l.id)) return
    go.disabled = true
    err.textContent = ''
    void mutate({
      label: `Unlink: ${l.name}`,
      method: 'POST',
      path: `/api/leads/${l.id}/unlink`,
      body: { version: l.version },
      onAck: (ack) => {
        if (gen !== routeGen || !onNewJobsRoute()) return
        const held = leads.get(l.id)
        const fresh = ack.row as Lead
        if (held && fresh.version < held.version) return
        leads.set(l.id, { ...held!, ...fresh })
        toast(`${l.name} is back in New jobs`)
      },
    }).then((ack) => {
      if (closed) return
      // Saved, or held to send when there is signal, or parked as changed elsewhere: the job's own row and the
      // New jobs banner say which from here.
      if (ack.ok || ack.status === 409 || getQueue().some((m) => m.path === `/api/leads/${l.id}/unlink`)) {
        finish(true)
        return
      }
      go.disabled = false
      err.textContent = ack.error ?? 'Not saved. Try again.'
    })
  })
  cancel.addEventListener('click', () => finish(false))
  dlg.addEventListener('cancel', (e) => {
    e.preventDefault()
    finish(false)
  })
  dlg.append(
    el('div', { class: 'newjobs-convert-body' },
      el('h3', {}, `Unlink ${l.name} from ${who}?`),
      el('p', {}, `Use this if ${l.name} is a different person, such as ${who}'s neighbour. ${Who}'s page stays exactly as it is.`),
      el('p', { class: 'muted' }, `${l.name} goes back to New jobs. You can then make them a separate customer.`)),
    el('div', { class: 'booking-footer' }, err, go, cancel),
  )
  document.body.append(dlg)
  dlg.showModal()
  go.focus()
}

// A dialog left open across a navigation must vanish with the route it
// belonged to — silently (no focus steal), leaving no stale editing flag
// and no queue forwarder pointed at a dead dialog. Covers the edit dialog
// and the convert dialog alike (both carry the newjobs-dialog class).
window.addEventListener('hashchange', () => {
  if (!editing) return
  const dlg = document.querySelector<HTMLDialogElement>('dialog.newjobs-dialog')
  if (dlg) {
    dlg.close()
    dlg.remove()
  }
  editing = false
  refreshPending = false
  dialogQueueChanged = null
})

// ---- queued-work banner -------------------------------------------------------

// A change that has not landed must be visible FROM this screen, not only on
// the corner chip — and a queued ADD must not invite a second, duplicate one.
function renderQueueBanner(root: HTMLElement): HTMLElement | null {
  const q = leadQueue()
  if (q.length === 0) return null
  const banner = el('div', { class: 'newjobs-queue-banner', role: 'status' })
  banner.append(el('strong', {}, `${q.length} change${q.length > 1 ? 's' : ''} to New jobs not saved yet. `))
  const labels = q.map((m) => `${m.label} (${m.status === 'queued' ? 'waiting for signal' : m.status === 'sending' ? 'saving' : m.status})`)
  banner.append(el('span', {}, labels.join(' · ')))
  root.prepend(banner)
  return banner
}

function addBlocked(): boolean {
  // One pending creation at a time: a second "add" queued behind the first
  // would create the same lead twice when signal returns.
  return leadQueue().some((m) => m.method === 'POST' && m.path === '/api/leads' && m.status !== 'failed' && m.status !== 'conflict')
}

// ---- render / load -------------------------------------------------------------

// A background load (queue flush, reconnect) rebuilds the WHOLE screen. The
// focused control and the table's scroll must survive that rebuild — and
// whatever the user moved to (search, another row, a filter) must be what
// focus lands back on, never a steal to somewhere else. Only elements inside
// THIS screen are captured; body focus captures nothing.
function captureFocusRestore(): (() => void) | null {
  const root = document.querySelector<HTMLElement>('#screen')
  const active = document.activeElement as HTMLElement | null
  if (!root || !active || !root.contains(active)) return null
  // Cards and the table both exist in the DOM. Restore the VISIBLE copy,
  // otherwise desktop focus attempts can target a hidden phone card.
  const visible = (selector: string): HTMLElement | undefined =>
    [...root.querySelectorAll<HTMLElement>(selector)].find(node => node.getClientRects().length > 0)
  const row = active.closest<HTMLElement>('[data-lead-id]')
  if (row && active.closest('.newjobs-status')) {
    const id = CSS.escape(row.dataset.leadId ?? '')
    return () => {
      const sel = visible(`[data-lead-id="${id}"] .newjobs-status-select`) as HTMLSelectElement | undefined
      const target = sel && !sel.disabled ? sel : visible(`[data-lead-id="${id}"] .newjobs-status`)
      // A successful status edit can remove the row from the current filter.
      ;(target ?? visible('select[aria-label="Filter by status"]'))?.focus({ preventScroll: true })
    }
  }
  const label = active.getAttribute('aria-label')
  const href = active.getAttribute('href')
  const sortKey = active.getAttribute('data-sort-key')
  const selector = label ? `[aria-label="${CSS.escape(label)}"]`
    : href ? `a[href="${CSS.escape(href)}"]`
    : sortKey ? `[data-sort-key="${CSS.escape(sortKey)}"]` : null
  if (!selector) return null
  const selection = active instanceof HTMLTextAreaElement ||
    (active instanceof HTMLInputElement && ['text', 'search', 'tel', 'url'].includes(active.type))
    ? { start: active.selectionStart, end: active.selectionEnd } : null
  return () => {
    const target = visible(selector)
    target?.focus({ preventScroll: true })
    if (target && selection && selection.start !== null && selection.end !== null &&
        (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
      target.setSelectionRange(selection.start, selection.end)
    }
  }
}

function render(list: Lead[], root: HTMLElement): void {
  // Capture BEFORE the rebuild: what was focused, and where the table was
  // scrolled to. Both go back exactly where they were after re-render.
  const restoreFocus = captureFocusRestore()
  const prevWrap = document.querySelector<HTMLElement>('#newjobs-table')
  const keepScroll = prevWrap ? { top: prevWrap.scrollTop, left: prevWrap.scrollLeft } : null
  const fresh = list.map(l => newerLead(l, leads.get(l.id)))
  // A lead gaining its live link in this render is a conversion that just
  // landed — this session's dialog, or an offline intent the queue flushed:
  // its row keeps the optional Book first visit shortcut beside the link.
  for (const l of fresh) {
    if (l.live_customer_id && leads.has(l.id) && !leads.get(l.id)?.live_customer_id) freshConversions.add(l.live_customer_id)
  }
  // A win that was queued (no signal) and has saved since: say so now.
  for (const l of fresh) {
    if (pendingWins.has(l.id) && normalizeLeadStatus(l.status_raw) === 'confirmed' && !leadBusy(l.id)) {
      pendingWins.delete(l.id)
      toast(WIN_TOAST(l.name))
    }
  }
  leads.clear()
  for (const l of fresh) leads.set(l.id, l)
  clear(root)
  const headActs = el('div', { class: 'page-heading-acts' })
  root.append(
    el(
      'div',
      { class: 'page-heading', id: 'newjobs-heading' },
      el('div', {}, el('h2', {}, 'New jobs'), el('p', { class: 'muted' }, fullSheet ? 'Every enquiry, every column: sort, filter and tidy up.' : 'Answer new customers first, then chase anyone who has not replied.')),
      headActs,
    ),
  )
  // Quote alerts: what this phone does when an enquiry or reply comes in; built once per visit to New jobs. On the list
  // it sits at the bottom, below the people (Liam's screenshot, 10 Oct 2026: the top of the page was all settings).
  alertsRow ??= enquiryAlertsRow()
  if (alertsRow && fullSheet) root.append(alertsRow)

  const bar = el('div', { class: 'screen-bar', id: 'newjobs-bar' })
  const search = el('input', { type: 'search', class: 'search-box', placeholder: 'Name, address, phone or notes', 'aria-label': 'Search new jobs' })
  search.value = filters.search
  // The count strip: one chip per colour, counts over the whole sheet, tap to
  // show just those. The status dropdown below follows it.
  const chips = el('div', { class: 'rebook-chips newjobs-chips', role: 'group', 'aria-label': 'Show jobs by status' })
  const countTone = (t: LeadTone | ''): number => [...leads.values()].filter((l) => t === '' || leadTone(l) === t).length
  const drawChips = (): void => {
    clear(chips)
    for (const [v, label] of STATUS_LABELS) {
      const n = countTone(v)
      if (v !== '' && n === 0) continue
      const chip = el('button', { type: 'button', class: `rebook-chip newjobs-chip newjobs-chip-${v || 'all'}${filters.status === v ? ' rebook-chip-on' : ''}`, 'aria-pressed': filters.status === v ? 'true' : 'false' }, `${v === '' ? 'All' : label} ${n}`)
      chip.addEventListener('click', () => { filters.status = v; statusSel.value = v; refreshList() })
      chips.append(chip)
    }
  }
  const refreshList = (): void => {
    renderTable(document.querySelector<HTMLElement>('#newjobs-table')!)
    renderCount()
    drawChips()
  }
  search.addEventListener('input', () => {
    filters.search = search.value
    refreshList()
  })
  const select = (label: string, entries: [string, string][], value: string, update: (v: string) => void): HTMLSelectElement => {
    const s = el('select', { 'aria-label': label })
    for (const [v, text] of entries) s.append(el('option', { value: v }, text))
    s.value = value
    s.addEventListener('change', () => {
      update(s.value)
      refreshList()
    })
    return s
  }
  const statusEntries: [string, string][] = STATUS_LABELS.map(([v, label]) => [v, v === '' ? label : `${label} · ${countTone(v)}`])
  const statusSel = select('Filter by status', statusEntries, filters.status, (v) => (filters.status = v as LeadFilters['status']))
  const checkedSel = select(
    'Filter by checked',
    [
      ['', 'Checked: all'],
      ['yes', 'Checked ✓'],
      ['no', 'Not checked'],
    ],
    filters.checked,
    (v) => (filters.checked = v as LeadFilters['checked']),
  )
  const sort = select('Sort new jobs', SORT_OPTIONS.map(([v, label]): [string, string] => [v, label.replace('£', currencySymbol())]), sortBy, (v) => (sortBy = v as LeadSortKey))
  const dirBtn = el(
    'button',
    { type: 'button', class: 'sort-dir', 'data-sort-dir': 'true', 'aria-label': `Sort direction: ${sortDir === 'desc' ? 'descending' : 'ascending'}. Activate to reverse.` },
    sortDir === 'desc' ? '↓ descending' : '↑ ascending',
  )
  dirBtn.addEventListener('click', () => {
    sortDir = sortDir === 'asc' ? 'desc' : 'asc'
    refreshList()
    syncSortControls()
  })
  const reset = el('button', { type: 'button' }, 'Clear filters')
  reset.addEventListener('click', () => {
    followupOnly = false
    filters = { search: '', status: '', checked: '' }
    render([...leads.values()], root)
  })
  const toggle = el('button', { type: 'button', 'aria-pressed': String(fullSheet) }, fullSheet ? 'Back to the list' : 'Full sheet')
  toggle.addEventListener('click', () => {
    fullSheet = !fullSheet
    render([...leads.values()], root)
  })
  const addBtn = el('button', { type: 'button', class: 'btn-primary', 'data-newjobs-add': 'true' }, '+ Add new job')
  if (addBlocked()) {
    addBtn.setAttribute('disabled', 'disabled')
    addBtn.title = 'A new job is still waiting to save. One at a time.'
  }
  addBtn.addEventListener('click', () => openDialog(null, addBtn))
  const follow = select('Filter follow-ups', [['', 'Follow-ups: all'], ['due', 'Follow up due']], followupOnly ? 'due' : '', v => { followupOnly = v === 'due' })
  headActs.append(toggle, addBtn)
  // The list sorts itself (who to answer first, then who to chase), so it needs only the search box (Liam, 10 Oct 2026:
  // "not sure how much is needed for new jobs"). Every filter and sort stays on the full sheet.
  if (fullSheet) bar.append(search, statusSel, checkedSel, follow, sort, dirBtn, reset)
  else {
    if (filters.status) filters.status = ''
    filters.checked = ''
    followupOnly = false
    bar.append(search)
  }

  const tableWrap = el('div', { id: 'newjobs-table', class: `sheet-wrap newjobs-wrap${fullSheet ? ' full-sheet' : ' board-mode'}` })
  const cards = el('div', { id: 'newjobs-cards', class: `newjobs-cards${fullSheet ? ' hide-cards' : ' board-on'}` })
  drawChips()
  if (fullSheet) root.append(chips)
  const count = el('p', { class: 'bar-note', id: 'newjobs-count', role: 'status' })
  count.hidden = !fullSheet
  root.append(bar, count, cards, tableWrap, el('p', { id: 'newjobs-empty', class: 'empty-note' }))
  if (alertsRow && !fullSheet) root.append(alertsRow)
  renderQueueBanner(root)
  renderTable(tableWrap)
  renderCount()
  if (keepScroll) {
    tableWrap.scrollTop = keepScroll.top
    tableWrap.scrollLeft = keepScroll.left
  }
  restoreFocus?.()
}

async function load(): Promise<void> {
  const gen = routeGen // navigated away mid-load → never render into the new screen
  const seq = ++loadSeq // a second load on the same route supersedes this one
  const root = document.querySelector<HTMLElement>('#screen')!
  const current = (): boolean => gen === routeGen && seq === loadSeq && onNewJobsRoute()
  try {
    const data = await api<{ leads: Lead[] }>('/api/leads', 'leads')
    if (!current()) return
    if (editing) {
      refreshPending = true
      return
    }
    render(data.leads, root)
  } catch (err) {
    if (!current()) return
    if (editing) {
      // an editor is open: a failed background read must not rebuild the UI
      // underneath it — defer, exactly like a successful one.
      refreshPending = true
      return
    }
    const cached = readCache<{ leads: Lead[] }>('leads')
    if (cached) {
      render(cached.data.leads, root)
      loadError(root, (err as Error).message, () => void load(), cached.at)
    } else {
      clear(root)
      loadError(root, (err as Error).message, () => void load())
    }
  }
}

export function mountNewJobs(): void {
  // a dialog must never survive its route; the hashchange listener closes it,
  // but a cold remount clears any residue too
  const leftover = document.querySelector<HTMLDialogElement>('dialog.newjobs-dialog')
  if (leftover) {
    leftover.close()
    leftover.remove()
  }
  editing = false
  refreshPending = false
  dialogQueueChanged = null
  alertsRow = null
  // Each visit starts on the list (a computer opens the first to answer beside it).
  openLead = null
  openClosedByHand = false
  // A phone alert opens the enquiry it was about (#/new-jobs?lead=<id>), as Needs you's "Open in New jobs" does.
  const asked = /[?&]lead=(\d{1,12})/.exec(location.hash)
  if (asked) revealLead = Number(asked[1])
  void load()
}
let alertsRow: HTMLElement | null = null

export function newJobsOnDataChanged(): void {
  if (editing) refreshPending = true
  else void load()
}
